console.log('🚀 Product Configurator Script Executing...');

// Options that describe how a screen looks and is driven, i.e. the ones that are
// normally identical across every screen in an order. Everything derived from the
// individual window (dimensions, cassette size, brackets, measurement guarantee)
// is deliberately left out so "apply to all" never breaks a screen's fit.
const SHARED_OPTION_FIELDS = ['fabricType', 'fabricColor', 'frameColor', 'motor', 'motorPlacement', 'cableExit'];

// Shipping tiers, mirroring the Shopify delivery profile ("General profile").
// Shopify charges these at checkout; what we do here is name the real amount
// beforehand instead of only advertising a free-shipping threshold the customer
// is about to miss. Change a rate in Shopify, change it here.
//
//   DE/AT zone : €89 up to €910, €39 up to €960, free above €960.
//   Norway zone: NOK 3,000 flat, free from NOK 30,000.
//
// `maxTotal` is an INCLUSIVE minor-unit upper bound, compared against the same
// discounted cart subtotal Shopify evaluates (screens + accessories + DIY
// brackets − volume discount). It cannot account for a discount code entered at
// checkout, so treat the free tier as "expected", not guaranteed.
//
// `freeThreshold.inclusive` distinguishes the two zones' boundaries: Norway goes
// free AT 30,000 ("fra"), DE/AT only ABOVE 960 ("über"), because that is how the
// two Shopify rates are actually configured.
//
// TODO(shipping): Norway's NOK 3,000 is under review — actual collection averages
// far less because sales waives it. Only these numbers need to change; the copy
// below is generated from them.
const SHIPPING_TIERS = {
  EUR: {
    tiers: [
      { maxTotal: 91000, price: 8900 },
      { maxTotal: 96000, price: 3900 },
      { maxTotal: Infinity, price: 0 },
    ],
    freeThreshold: { display: 96000, inclusive: false },
  },
  NOK: {
    tiers: [
      { maxTotal: 2999999, price: 300000 },
      { maxTotal: Infinity, price: 0 },
    ],
    freeThreshold: { display: 3000000, inclusive: true },
  },
};

class ProductConfigurator extends HTMLElement {
  constructor() {
    super();
    this.state = {
      screens: [], // Array of screen objects
      installationType: 'diy',
      brackets: {}, // { prodId: qty }
      steuerung: {}, // { prodId: qty }
      automatisierung: {}, // { prodId: qty }
      showBaselineWizard: false,
      tempImportedWindows: null,
      baselineFrameColor: null,
      baselineFabricColor: null,
      baselineFabricType: null,
      baselineMotor: null,
      showCalculationModalIndex: null,
      appliedOptionsToAllId: null,
      depositEnabled: true, // "X % now, rest before shipping" — only effective when a deposit plan exists
    };
  }

  /* --- Slider Navigation Helpers --- */

  /**
   * Jump to a specific slide by its ID fragment (e.g., 'svg-configurator' or media ID)
   */
  jumpToSlide(slideIdFragment) {
    const slide = document.querySelector(`[id*="${slideIdFragment}"]`);
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

      // Update thumbnail active state
      const gallery = document.querySelector('media-gallery');
      if (gallery) {
        const thumbnails = gallery.querySelectorAll('.thumbnail');
        thumbnails.forEach((thumb) => {
          if (thumb.dataset.target && thumb.dataset.target.includes(slideIdFragment)) {
            thumbnails.forEach((t) => t.classList.remove('active'));
            thumb.classList.add('active');
          }
        });
      }
    }
  }

  /**
   * Jump to the SVG configurator slide
   */
  jumpToSVGConfigurator() {
    this.jumpToSlide('svg-configurator');
  }

  /**
   * Get the currently active/expanded screen, or the first screen as fallback
   */
  getActiveScreen() {
    if (this.state.showBaselineWizard) {
      return {
        width: 800,
        height: 600,
        frameColor: this.state.baselineFrameColor,
        fabricColor: this.state.baselineFabricColor,
        fabricType: this.state.baselineFabricType,
        cassetteSize: 'slim',
        motor: this.state.baselineMotor,
        cableExit: 'back',
      };
    }
    const activeScreen = this.state.screens.find((s) => s.expanded);
    return activeScreen || this.state.screens[0] || null;
  }

  /**
   * Jump to the first product image slide and update it with the dynamic image
   * Uses the currently active/expanded screen's options
   */
  jumpToFirstSlideWithImage(overrideImageUrl) {
    const activeScreen = this.getActiveScreen();
    if (!activeScreen) return;

    // Use an explicit image when provided (e.g. the fabric close-up shown on a
    // transparency change), otherwise the dynamic product render for the selection.
    const imageUrl = overrideImageUrl || this.buildDynamicImageUrl(activeScreen);

    if (imageUrl) {
      // Find and update the first product image
      this.updateFirstProductImage(imageUrl);
    }

    // Jump to the first slide (not SVG configurator)
    const slider = document.querySelector('[id*="Slider-Gallery-"]');
    if (slider) {
      const firstSlide = slider.querySelector('.slider__slide:first-child');
      if (firstSlide) {
        firstSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

        // Update thumbnail active state - find the first non-configurator thumbnail
        const gallery = document.querySelector('media-gallery');
        if (gallery) {
          const thumbnails = gallery.querySelectorAll('.thumbnail');
          thumbnails.forEach((t) => t.classList.remove('active'));
          const firstThumb = gallery.querySelector('.thumbnail:not(.thumbnail--configurator)');
          if (firstThumb) {
            firstThumb.classList.add('active');
          }
        }
      }
    }
  }

  /**
   * Build the dynamic product image URL based on selected options
   * Uses pre-generated Shopify CDN URLs from ScreenluxData.assets.productImages
   */
  buildDynamicImageUrl(screen) {
    // Map option IDs to codes
    const frameCodeMap = {
      anthracite: 'AN',
      white: 'WH',
      black: 'BL',
    };

    const fabricColorCodeMap = {
      graphite: 'BLG',
      'gray-pepper': 'GRP',
      natural: 'NAT',
      edelweiss: 'EWH',
    };

    const fabricTypeCodeMap = {
      '5-percent': '5',
      blackout: '0',
    };

    const motorCodeMap = {
      solar: 'DC',
      wired: 'AC',
    };

    // Get codes with defaults
    const frameCode = frameCodeMap[screen.frameColor] || 'AN';
    const fabricColorCode = fabricColorCodeMap[screen.fabricColor] || 'BLG';
    const fabricTypeCode = fabricTypeCodeMap[screen.fabricType] || '5';
    const motorCode = motorCodeMap[screen.motor] || 'DC';

    // Build the key to look up in productImages
    const imageKey = `${motorCode}-${frameCode}-${fabricColorCode}-${fabricTypeCode}`;

    // Get the URL from ScreenluxData.assets.productImages
    if (this.data && this.data.assets && this.data.assets.productImages) {
      const imageUrl = this.data.assets.productImages[imageKey];
      if (imageUrl) {
        return imageUrl;
      }
    }

    // Fallback - return null if image not found
    console.warn(`Product image not found for key: ${imageKey}`);
    return null;
  }

  /**
   * Preload all product images for faster switching
   */
  preloadProductImages() {
    if (this.data && this.data.assets && this.data.assets.productImages) {
      Object.values(this.data.assets.productImages).forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    }
  }

  /**
   * Fabric colours available for a given transparency.
   * Blackout is offered only in graphite, natural and white (edelweiss relabelled);
   * gray-pepper is 5%-only. 5% keeps the original chips; blackout uses its dedicated chips.
   */
  getFabricColorsForType(fabricType) {
    const isBlackout = fabricType === 'blackout';
    // Blackout is offered only in Graphit and Weiß (Edelweiß relabelled) —
    // Grau-Pfeffer and Natur are 5%-only.
    return (this.data.fabricColors || [])
      .filter((c) => !(isBlackout && (c.id === 'gray-pepper' || c.id === 'natural')))
      .map((c) => ({
        ...c,
        title: isBlackout && c.blackoutTitle ? c.blackoutTitle : c.title,
        image: (isBlackout ? c.blackoutSwatch : c.image) || c.image,
      }));
  }

  /**
   * Product-gallery photo showing the fabric at the selected colour + transparency.
   * Shown when the transparency option changes (gray-pepper falls back to the BLA set).
   */
  getFabricGalleryImage(screen) {
    const colours = this.data.fabricColors || [];
    const selected = colours.find((c) => c.id === screen.fabricColor) || colours[0];
    const code = (selected && selected.previewCode) || 'BLA';
    const t = screen.fabricType === 'blackout' ? '0' : '5';
    const previews = (this.data.assets && this.data.assets.fabricPreviewImages) || {};
    return previews[`${code}-${t}`] || null;
  }

  /**
   * Keep the selected fabric colour valid for the chosen transparency,
   * moving to the nearest available colour (graphite) when it is not.
   */
  nearestFabricColorForType(fabricColor, fabricType) {
    const available = this.getFabricColorsForType(fabricType);
    if (available.some((c) => c.id === fabricColor)) return fabricColor;
    return available.length ? available[0].id : fabricColor;
  }

  /**
   * Setup mobile sticky gallery behavior
   * Gallery image stays fixed at top on mobile once user scrolls past it,
   * until reaching the installation section where it fades out.
   */
  setupMobileStickyGallery() {
    // Only apply on mobile
    const isMobile = () => window.innerWidth <= 749;

    const gallery = document.querySelector('.screenlux-gallery');
    if (!gallery) return;

    const sliderComponent = gallery.querySelector('slider-component');
    if (!sliderComponent) return;

    let isCurrentlySticky = false;
    let isFading = false;
    let galleryOriginalTop = null;
    let wasDismissed = false;

    // Set the CSS variable for placeholder height
    const updateSliderHeight = () => {
      if (sliderComponent) {
        const height = sliderComponent.offsetHeight;
        gallery.style.setProperty('--mobile-slider-height', `${height}px`);
      }
    };

    // Get gallery's original position (call before making it sticky)
    const getGalleryTop = () => {
      if (galleryOriginalTop === null) {
        // Store the original top position relative to document
        galleryOriginalTop = gallery.getBoundingClientRect().top + window.scrollY;
      }
      return galleryOriginalTop;
    };

    // Function to update sticky state based on scroll position
    const updateStickyState = () => {
      if (!isMobile()) {
        gallery.classList.remove('mobile-sticky', 'fading-out', 'fading-in');
        isCurrentlySticky = false;
        galleryOriginalTop = null; // Reset for recalculation
        return;
      }

      const scrollY = window.scrollY;
      const originalTop = getGalleryTop();
      const sliderHeight = sliderComponent ? sliderComponent.offsetHeight : 300;

      // Find the motor selector in the active screen container to use as an early fade-out trigger
      let triggerEl = null;
      const activeDetails = document.querySelector('details-accordion details[open]');
      if (activeDetails) {
        const motorSelector = activeDetails.querySelector('[data-field-name="motor"]');
        if (motorSelector) {
          triggerEl = motorSelector;
        }
      }

      // Fallback to installation section if active screen motor selector is not found
      if (!triggerEl) {
        triggerEl = document.getElementById('installation-section');
      }

      // Check if we should stop being sticky (scrolled past the motor selector or approaching installation)
      if (triggerEl) {
        const triggerRect = triggerEl.getBoundingClientRect();
        const isMotorSelector = triggerEl.dataset.fieldName === 'motor';
        const shouldFadeOut = isMotorSelector
          ? (triggerRect.bottom <= sliderHeight + 30)
          : (triggerRect.top <= sliderHeight + 50);

        if (shouldFadeOut) {
          if (isCurrentlySticky && !isFading) {
            // Start fade out - mark as dismissed so we fade in when returning
            isFading = true;
            wasDismissed = true;
            gallery.classList.add('fading-out');

            // After fade animation, remove sticky class
            setTimeout(() => {
              gallery.classList.remove('mobile-sticky', 'fading-out');
              isCurrentlySticky = false;
              isFading = false;
            }, 300);
          }
          return;
        }
      }

      // Check if we should become sticky (scrolled past gallery's original position)
      if (scrollY > originalTop) {
        if (!isCurrentlySticky && !isFading) {
          updateSliderHeight();

          // If we were dismissed, fade in when re-appearing
          if (wasDismissed) {
            gallery.classList.add('mobile-sticky', 'fading-in');
            gallery.classList.remove('fading-out');
            isCurrentlySticky = true;

            // Remove fading-in class after animation completes
            setTimeout(() => {
              gallery.classList.remove('fading-in');
              wasDismissed = false;
            }, 300);
          } else {
            // First time becoming sticky - no animation
            gallery.classList.add('mobile-sticky');
            gallery.classList.remove('fading-out');
            isCurrentlySticky = true;
          }
        }
      } else {
        // Should NOT be sticky - gallery is still in view
        if (isCurrentlySticky && !isFading) {
          gallery.classList.remove('mobile-sticky', 'fading-out', 'fading-in');
          isCurrentlySticky = false;
        }
      }
    };

    // Listen to scroll events with throttling
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateStickyState();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Also listen to resize in case viewport changes
    window.addEventListener('resize', () => {
      galleryOriginalTop = null; // Reset for recalculation
      if (!isMobile()) {
        gallery.classList.remove('mobile-sticky', 'fading-out');
        isCurrentlySticky = false;
        isFading = false;
      } else {
        updateSliderHeight();
        updateStickyState();
      }
    });

    // Initial check (in case page loads already scrolled)
    if (isMobile()) {
      setTimeout(() => {
        updateSliderHeight();
        updateStickyState();
      }, 100);
    }
  }

  /**
   * Update the first product image in the gallery with the dynamic URL
   * Includes a smooth fade transition
   */
  updateFirstProductImage(imageUrl) {
    if (!imageUrl) return;

    const slider = document.querySelector('[id*="Slider-Gallery-"]');
    if (!slider) return;

    const firstSlide = slider.querySelector('.slider__slide:first-child');
    if (!firstSlide) return;

    const img = firstSlide.querySelector('img.gallery-image');
    if (img) {
      // Add transition class if not already present
      if (!img.classList.contains('dynamic-image-transition')) {
        img.classList.add('dynamic-image-transition');
      }

      // Fade out, change image, fade in
      img.style.opacity = '0';

      setTimeout(() => {
        img.src = imageUrl;
        img.srcset = `${imageUrl} 1066w`;

        // Wait for image to load then fade in
        if (img.complete) {
          img.style.opacity = '1';
        } else {
          img.onload = () => {
            img.style.opacity = '1';
          };
        }
      }, 150);
    }
  }

  saveStateToStorage() {
    try {
      if (this.state.showBaselineWizard) return; // Do not save configurator state while baseline wizard is active
      const stateToSave = {
        screens: this.state.screens,
        installationType: this.state.installationType,
        brackets: this.state.brackets,
        steuerung: this.state.steuerung,
        automatisierung: this.state.automatisierung,
        baselineFrameColor: this.state.baselineFrameColor,
        baselineFabricColor: this.state.baselineFabricColor,
        baselineFabricType: this.state.baselineFabricType,
        baselineMotor: this.state.baselineMotor,
        depositEnabled: this.state.depositEnabled,
      };
      localStorage.setItem('screenlux_configurator_state', JSON.stringify(stateToSave));
    } catch (e) {
      console.error('Failed to save configurator state to localStorage:', e);
    }
  }

  computeWindowsHash(windows) {
    if (!windows || !Array.isArray(windows)) return '';
    try {
      const representation = windows.map(win => ({
        id: win.id,
        name: win.name || '',
        mountType: win.mountType || '',
        type: win.type || '',
        widthTop: win.widthTop || '',
        widthMiddle: win.widthMiddle || '',
        widthBottom: win.widthBottom || '',
        heightLeft: win.heightLeft || '',
        heightRight: win.heightRight || '',
        waterboardDistance: win.waterboardDistance || '',
        handleDistance: win.handleDistance || '',
        hasRoofOverhang: win.hasRoofOverhang || '',
        roofOverhangDistance: win.roofOverhangDistance || '',
        isEstimate: !!win.isEstimate,
        measurementGuarantee: !!win.measurementGuarantee
      }));
      return JSON.stringify(representation);
    } catch (e) {
      console.error('Failed to compute windows hash:', e);
      return '';
    }
  }

  loadStateFromStorage() {
    try {
      const savedState = localStorage.getItem('screenlux_configurator_state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed && typeof parsed === 'object') {
          if (Array.isArray(parsed.screens)) {
            this.state.screens = parsed.screens;
          }
          if (parsed.installationType) {
            this.state.installationType = parsed.installationType;
          }
          if (typeof parsed.depositEnabled === 'boolean') {
            this.state.depositEnabled = parsed.depositEnabled;
          }
          if (parsed.brackets) {
            this.state.brackets = parsed.brackets;
          }
          if (parsed.steuerung) {
            this.state.steuerung = parsed.steuerung;
          }
          if (parsed.automatisierung) {
            this.state.automatisierung = parsed.automatisierung;
          }
          if (parsed.baselineFrameColor) {
            this.state.baselineFrameColor = parsed.baselineFrameColor;
          }
          if (parsed.baselineFabricColor) {
            this.state.baselineFabricColor = parsed.baselineFabricColor;
          }
          if (parsed.baselineFabricType) {
            this.state.baselineFabricType = parsed.baselineFabricType;
          }
          if (parsed.baselineMotor) {
            this.state.baselineMotor = parsed.baselineMotor;
          }
        }
      }
    } catch (e) {
      console.error('Failed to load configurator state from localStorage:', e);
    }
  }

  async fetchAndApplyDatabaseMeasurements() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const customerId = urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || this.dataset.customerId;
      if (!customerId) return;

      this.state.dbSyncStatus = 'loading';
      this.state.dbSyncError = null;
      this.render();

      const res = await fetch(`/apps/measurements?customer_id=${customerId}&logged_in_customer_id=${customerId}&_=${Date.now()}`);
      if (res.ok) {
        const data = await res.json();
        const windows = data && data.windows;
        if (Array.isArray(windows) && windows.length > 0) {
          this.state.dbSyncStatus = 'success';
          console.log('Successfully fetched saved windows for customer:', windows);
          
          const isDefaultScreen = this.state.screens.length === 1 && 
                                  this.state.screens[0].width === 800 && 
                                  this.state.screens[0].height === 600 &&
                                  !this.state.screens[0].name &&
                                  !this.state.screens[0].measurementGuarantee;

          if (this.state.screens.length === 0 || isDefaultScreen) {
            this.state.tempImportedWindows = windows;
            this.confirmBaselineWizard();
          } else {
            const newHash = this.computeWindowsHash(windows);
            let lastHash = localStorage.getItem('screenlux_last_imported_measurements_hash');

            if (!lastHash && localStorage.getItem('screenlux_configurator_state')) {
              localStorage.setItem('screenlux_last_imported_measurements_hash', newHash);
              console.log('Migration: Saved current database measurements hash. Skipping overwrite to preserve existing local customizations.');
              this.render();
              return;
            }

            if (newHash === lastHash) {
              console.log('Database measurements match last imported hash. Skipping overwrite to preserve local customizations.');
              this.render();
              return;
            }

            console.log('Database measurements changed. Overwriting and syncing configurator screens.');
            this.state.tempImportedWindows = windows;
            this.confirmBaselineWizard();
          }
        } else {
          this.state.dbSyncStatus = 'no_measurements';
          this.render();
        }
      } else {
        this.state.dbSyncStatus = 'error';
        this.state.dbSyncError = `HTTP ${res.status} ${res.statusText}`;
        this.render();
      }
    } catch (err) {
      this.state.dbSyncStatus = 'error';
      this.state.dbSyncError = err.message || err.toString();
      this.render();
      console.error('Failed to fetch and apply database measurements:', err);
    }
  }

  connectedCallback() {
    this.init();
    
    // Add real-time sync when switching back to the configurator tab
    window.addEventListener('focus', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const customerId = urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || this.dataset.customerId;
      if (customerId) {
        this.fetchAndApplyDatabaseMeasurements();
      }
    });
  }

  init() {
    try {
      // 1. Load Data
      this.data = window.ScreenluxData;
      if (!this.data) {
        throw new Error('ScreenluxData is undefined. Check console.');
      }

      // Initialize baseline option states from configurator metadata
      this.state.baselineFrameColor = this.data.frameColors?.length > 0 ? this.data.frameColors[0].id : 'anthracite';
      this.state.baselineFabricColor = this.data.fabricColors?.length > 0 ? this.data.fabricColors[0].id : 'graphite';
      this.state.baselineFabricType = this.data.fabrics?.length > 0 ? this.data.fabrics[0].id : '5-percent';
      this.state.baselineMotor = this.data.motorOptions?.length > 0 ? this.data.motorOptions[0].id : 'solar';

      // Load preserved state from localStorage if it exists
      this.loadStateFromStorage();

      // 0. Base Validation Check - "Unsure" injection removed as requested

      // 2. Initial State: Load from Measuring App if transfer flag is set, otherwise default 1 Screen
      const transferFlag = localStorage.getItem('screenlux_transfer_measurements');
      const savedMeasurements = localStorage.getItem('screenlux_measurements');
      const isEstimateTransfer = localStorage.getItem('screenlux_estimate_transfer') === 'true';
      let loadedFromImport = false;

      if (transferFlag === 'true' && savedMeasurements) {
        try {
          const windows = JSON.parse(savedMeasurements);
          if (Array.isArray(windows) && windows.length > 0) {
            this.state.tempImportedWindows = windows;
            loadedFromImport = true;
            this.state.showBaselineWizard = false;
            this.confirmBaselineWizard();
          }
        } catch (err) {
          console.error('Failed to parse screenlux_measurements:', err);
        }
      }

      if (!loadedFromImport) {
        if (this.state.screens.length === 0) {
          this.handleAddScreen(true);
        }
        // Load from database if customer is logged in or customerId is in URL, and no local transfer is active
        const urlParams = new URLSearchParams(window.location.search);
        const customerId = urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || this.dataset.customerId;
        if (customerId && transferFlag !== 'true') {
          this.fetchAndApplyDatabaseMeasurements();
        }
      }

      // 3. Initial Render
      this.render();

      // 4. Dispatch initial state for SVG configurator
      if (this.state.showBaselineWizard) {
        const mockScreen = this.getActiveScreen();
        this.updateSVGConfigurator(mockScreen);
        this.jumpToFirstSlideWithImage();
        document.dispatchEvent(new CustomEvent('screenlux:configurator-ready'));
      } else if (this.state.screens.length > 0) {
        this.updateSVGConfigurator(this.state.screens[0]);
        document.dispatchEvent(new CustomEvent('screenlux:configurator-ready'));
      }

      // 5. Preload all product images for faster switching
      this.preloadProductImages();

      // 6. Setup mobile sticky gallery behavior
      this.setupMobileStickyGallery();

      // 7. Setup sticky summary bar
      window.addEventListener('scroll', () => {
        if (!this._tickingStickyBar) {
          window.requestAnimationFrame(() => {
            if (this.updateStickyBar) this.updateStickyBar();
            this._tickingStickyBar = false;
          });
          this._tickingStickyBar = true;
        }
      });
      window.addEventListener('resize', () => {
        if (this.updateStickyBar) this.updateStickyBar();
      });

    } catch (err) {
      console.error('Configurator Init Error:', err);
      this.innerHTML = `
            <div style="padding: 20px; color: red; text-align: center; border: 1px solid red; margin: 20px;">
                <h3>Application Error</h3>
                <p>${err.message}</p>
                <small>${err.stack || ''}</small>
            </div>
        `;
    }
  }

  /* --- State Modifiers --- */

  updateSVGConfigurator(screen) {
    if (!screen) return;
    document.dispatchEvent(
      new CustomEvent('screenlux:screen-updated', {
        detail: {
          width: screen.width,
          height: screen.height,
          frameColor: screen.frameColor,
          fabricColor: screen.fabricColor,
          fabricType: screen.fabricType,
          cassetteSize: screen.cassetteSize,
          motor: screen.motor,
          cableExit: screen.cableExit,
        },
      }),
    );
  }

  handleAddScreen = (skipScroll = false) => {
    const newId = Date.now();
    
    // Auto-select baseline choices if set, otherwise first available options
    const frameColor = this.state.baselineFrameColor || (this.data.frameColors?.length > 0 ? this.data.frameColors[0].id : null);
    const fabricColor = this.state.baselineFabricColor || (this.data.fabricColors?.length > 0 ? this.data.fabricColors[0].id : null);
    const fabricType = this.state.baselineFabricType || (this.data.fabrics?.length > 0 ? this.data.fabrics[0].id : null);
    const cassetteSize = this.data.cassetteSizes?.length > 0 ? this.data.cassetteSizes[0].id : 'slim';
    const motor = this.state.baselineMotor || (this.data.motorOptions?.length > 0 ? this.data.motorOptions[0].id : null);
    const cableExit = motor === 'wired' ? 'back' : 'back'; // If cable, hinten (back) is the standard cable outlet
    const motorPlacement = motor === 'wired' ? 'left' : '';

    this.state.screens.push({
      id: newId,
      width: 800,
      height: 600,
      frameColor: frameColor,
      fabricColor: fabricColor,
      fabricType: fabricType,
      cassetteSize: cassetteSize,
      motor: motor,
      cableExit: cableExit,
      motorPlacement: motorPlacement,
      mountingBracket: 'none',
      measurementGuarantee: false,
      expanded: true,
      valid: true,
      errors: {},
    });

    // Collapse others
    this.state.screens.forEach((s) => {
      if (s.id !== newId) s.expanded = false;
    });

    this.render();

    // Scroll to the new screen (last one) - unless skipScroll is true
    if (!skipScroll) {
      setTimeout(() => {
        const screens = this.querySelectorAll('details-accordion');
        const lastScreen = screens[screens.length - 1];
        if (lastScreen) {
          lastScreen.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
    
    // Update SVG configurator
    const activeScreen = this.getActiveScreen();
    this.updateSVGConfigurator(activeScreen);
  };

  handleDuplicateScreen = (index) => {
    const source = this.state.screens[index];
    const newScreen = JSON.parse(JSON.stringify(source));
    newScreen.id = Date.now();
    newScreen.expanded = true;

    if (newScreen.name) {
      const isDE = (this.data.locale || '').toLowerCase().startsWith('de');
      const isNO = (this.data.locale || '').toLowerCase().startsWith('no') || (this.data.locale || '').toLowerCase().startsWith('nb') || this.data.config.currencyCode === 'NOK';
      const copySuffix = isDE ? ' (Kopie)' : (isNO ? ' (Kopi)' : ' (Copy)');
      const cleanName = newScreen.name.replace(/\s*\((Kopi|Kopie|Copy)\)$/i, '');
      newScreen.name = cleanName + copySuffix;
    }

    // Collapse all
    this.state.screens.forEach((s) => (s.expanded = false));

    this.state.screens.splice(index + 1, 0, newScreen);
    this.render();

    // Scroll to the new screen (index + 1)
    setTimeout(() => {
      const screens = this.querySelectorAll('details-accordion');
      const newScreenEl = screens[index + 1];
      if (newScreenEl) {
        newScreenEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
    
    // Update SVG configurator
    const activeScreen = this.getActiveScreen();
    this.updateSVGConfigurator(activeScreen);
  };

  updateScreen(index, field, value) {
    const screen = this.state.screens[index];

    // Type conversion for numbers
    if (field === 'width' || field === 'height') {
      value = parseInt(value) || 0;
      if (screen[field] !== value) {
        screen.measurementGuarantee = false;
        
        // Auto-restore guarantee if the typed values match the original values
        const otherField = field === 'width' ? 'height' : 'width';
        const otherVal = screen[otherField];
        const newW = field === 'width' ? value : otherVal;
        const newH = field === 'height' ? value : otherVal;
        if (screen.originalWidth && screen.originalHeight && newW === screen.originalWidth && newH === screen.originalHeight) {
          screen.measurementGuarantee = true;
        }
      }
    }

    screen[field] = value;

    // Blackout offers fewer colours; keep the selected colour valid.
    if (field === 'fabricType') {
      screen.fabricColor = this.nearestFabricColorForType(screen.fabricColor, value);
    }

    // Run validation logic
    const v = window.ScreenluxEngine.validateDimensions(screen.width, screen.height);
    screen.valid = v.valid;
    screen.errors = v.valid ? {} : { dimension: v.error };

    // Dispatch event for SVG configurator (always use active screen for preview)
    this.updateSVGConfigurator(screen);

    // Handle slider navigation based on what field changed
    const optionFields = ['frameColor', 'fabricColor', 'fabricType', 'motor'];
    const measurementFields = ['width', 'height'];

    if (measurementFields.includes(field)) {
      // Jump to SVG configurator when measurements change
      this.jumpToSVGConfigurator();
    } else if (optionFields.includes(field)) {
      // Transparency shows the fabric close-up; frame/fabric colour + motor show the
      // product render.
      const gallery = field === 'fabricType' ? this.getFabricGalleryImage(screen) : null;
      this.jumpToFirstSlideWithImage(gallery);
    }

    this.render();
  }

  restoreOriginalMeasurements(index) {
    const screen = this.state.screens[index];
    if (screen.originalWidth && screen.originalHeight) {
      screen.width = screen.originalWidth;
      screen.height = screen.originalHeight;
      screen.measurementGuarantee = true;

      // Run validation logic
      const v = window.ScreenluxEngine.validateDimensions(screen.width, screen.height);
      screen.valid = v.valid;
      screen.errors = v.valid ? {} : { dimension: v.error };

      // Dispatch event for SVG configurator
      this.updateSVGConfigurator(screen);

      this.render();
    }
  }

  removeScreen(index) {
    if (this.state.screens.length <= 1) return;
    this.state.screens.splice(index, 1);

    // Determines which screen to focus after deletion logic
    // We want to focus the *previous* screen if possible, or the one that took its place (if it was the first one).
    if (this.state.screens.length > 0) {
      let newFocusIndex = index - 1;
      if (newFocusIndex < 0) newFocusIndex = 0;

      // Ensure the screen we fallback to is expanded
      this.state.screens[newFocusIndex].expanded = true;
      // Ensure others are collapsed to keep it clean
      this.state.screens.forEach((s, i) => {
        if (i !== newFocusIndex) s.expanded = false;
      });
    }

    this.render();

    // Scroll into view logic
    if (this.state.screens.length > 0) {
      setTimeout(() => {
        let newFocusIndex = index - 1;
        if (newFocusIndex < 0) newFocusIndex = 0;

        const screens = this.querySelectorAll('details-accordion');
        const targetScreen = screens[newFocusIndex];
        if (targetScreen) {
          targetScreen.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      
      // Update SVG configurator
      const activeScreen = this.getActiveScreen();
      this.updateSVGConfigurator(activeScreen);
    }
  }

  toggleScreenAccordion(index) {
    // 1. Update internal state (for next render)
    const wasExpanded = this.state.screens[index].expanded;
    this.state.screens.forEach((s, i) => {
      s.expanded = i === index ? !s.expanded : false;
    });

    // 2. DOM Manipulation directly to avoid re-render killing animation
    const screens = this.querySelectorAll('details-accordion');

    screens.forEach((accordion, i) => {
      const details = accordion.querySelector('details');
      if (details) {
        if (i !== index) {
          // Close others if open
          if (details.open) {
            accordion.shrink();
          }
        }
        // For i === index, we let details-accordion handle the toggle itself
      }
    });

    // 3. Update the product image to reflect the newly expanded screen's options
    if (!wasExpanded && this.state.screens[index].expanded) {
      // Screen was just opened - update the product image
      const openedScreen = this.state.screens[index];
      const imageUrl = this.buildDynamicImageUrl(openedScreen);
      if (imageUrl) {
        this.updateFirstProductImage(imageUrl);
      }
      
      // Update SVG configurator to match expanded screen
      this.updateSVGConfigurator(openedScreen);
    }

    // We do NOT call this.render() here to preserve the DOM for animation.
    // The state is updated so next time something else triggers render, it will be correct.
  }

  setInstallationType(type) {
    this.state.installationType = type;
    this.render();
  }

  handleGlobalSolar = () => {
    this.state.screens.forEach((s) => (s.motor = 'solar'));
    this.render();
  };

  handleApplyOptionsToAll = (index) => {
    const source = this.state.screens[index];
    if (!source) return;

    this.state.screens.forEach((screen, i) => {
      if (i === index) return;
      SHARED_OPTION_FIELDS.forEach((field) => {
        screen[field] = source[field];
      });
    });

    // Show a short confirmation on the button that was pressed.
    this.state.appliedOptionsToAllId = source.id;
    clearTimeout(this._applyToAllTimer);
    this._applyToAllTimer = setTimeout(() => {
      this.state.appliedOptionsToAllId = null;
      this.render();
    }, 2500);

    this.render();
  };

  renderBaselineWizard(texts) {
    const container = document.createElement('div');
    container.className = 'configurator-app';

    const wizardGroup = document.createElement('div');
    wizardGroup.className = 'configurator-group-box';

    wizardGroup.innerHTML = `
      <!-- Fabric Transparency Selector (function first) -->
      ${this.renderSelectionGrid(
        window.ScreenluxTranslations.options.fabricTransparency,
        'baselineFabricType',
        this.data.fabrics || [],
        this.state.baselineFabricType,
        'baseline',
        'image',
        'vertical'
      )}

      <!-- Frame Color Selector -->
      ${this.renderSelectionGrid(
        window.ScreenluxTranslations.options.frameColor,
        'baselineFrameColor',
        this.data.frameColors || [],
        this.state.baselineFrameColor,
        'baseline',
        'color',
        'horizontal'
      )}

      <!-- Fabric Color Selector -->
      ${this.renderSelectionGrid(
        window.ScreenluxTranslations.options.fabricColor,
        'baselineFabricColor',
        this.getFabricColorsForType(this.state.baselineFabricType),
        this.state.baselineFabricColor,
        'baseline',
        'color',
        'horizontal'
      )}

      <!-- Motor Selector -->
      ${this.renderSelectionGrid(
        window.ScreenluxTranslations.options.motor,
        'baselineMotor',
        this.data.motorOptions || [],
        this.state.baselineMotor,
        'baseline',
        'image',
        'vertical'
      )}

      <div style="margin-top: 32px;">
        <button type="button" class="btn btn-primary confirm-baseline-btn" style="width: 100%; padding: 16px 24px; font-size: 16px; font-weight: 600;">
          ${texts.confirmBtn}
        </button>
      </div>
    `;

    // Event Delegation for inputs
    wizardGroup.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', (e) => {
        const field = e.target.dataset.field; // e.g. 'baselineFrameColor'
        if (field) {
          this.state[field] = e.target.value;

          // Blackout offers fewer colours; keep the selected colour valid.
          if (field === 'baselineFabricType') {
            this.state.baselineFabricColor = this.nearestFabricColorForType(
              this.state.baselineFabricColor,
              this.state.baselineFabricType
            );
          }

          // Update SVG and dynamic product image preview
          const mockScreen = this.getActiveScreen();
          this.updateSVGConfigurator(mockScreen);
          const gallery = field === 'baselineFabricType' ? this.getFabricGalleryImage(mockScreen) : null;
          this.jumpToFirstSlideWithImage(gallery);

          this.render();
        }
      });
    });

    // Confirm button listener
    const confirmBtn = wizardGroup.querySelector('.confirm-baseline-btn');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', (e) => {
        e.preventDefault();
      this.confirmBaselineWizard();
      });
    }

    container.appendChild(wizardGroup);
    return container;
  }

  confirmBaselineWizard() {
    try {
      const windows = this.state.tempImportedWindows;
      if (!windows) return;

      const isEstimateTransfer = localStorage.getItem('screenlux_estimate_transfer') === 'true';
      const localeCode = (this.data.locale || '').toLowerCase();
      const isDE = localeCode.startsWith('de');
      const isNO = localeCode.startsWith('no') || localeCode.startsWith('nb') || (this.data.config && this.data.config.currencyCode === 'NOK');

      const isDefaultScreen = this.state.screens.length === 1 &&
                              this.state.screens[0].width === 800 &&
                              this.state.screens[0].height === 600 &&
                              !this.state.screens[0].name &&
                              !this.state.screens[0].measurementGuarantee;

      // Keep whichever screen the customer already had open (background re-sync).
      // On a fresh import from the measuring app nothing is open yet, so we open
      // the first screen and the customer can start picking options right away.
      const previouslyExpandedIndex = this.state.screens.findIndex((s) => s.expanded);

      this.state.screens = windows.map((win, idx) => {
        const existingScreen = (!isDefaultScreen && this.state.screens && this.state.screens[idx]) ? this.state.screens[idx] : null;

        const frameColor = existingScreen ? existingScreen.frameColor : (isEstimateTransfer ? (win.suggestedFrameColor || 'anthracite') : this.state.baselineFrameColor);
        const fabricColor = existingScreen ? existingScreen.fabricColor : this.state.baselineFabricColor;
        const fabricType = existingScreen ? existingScreen.fabricType : this.state.baselineFabricType;
        const motor = existingScreen ? existingScreen.motor : (isEstimateTransfer ? (win.suggestedMotor || 'solar') : this.state.baselineMotor);
        const cableExit = existingScreen ? existingScreen.cableExit : (motor === 'wired' ? 'back' : 'back');
        const motorPlacement = existingScreen ? existingScreen.motorPlacement : (motor === 'wired' ? 'left' : '');
        const mountingBracket = existingScreen ? existingScreen.mountingBracket : 'none';
        const cassetteSizePreset = existingScreen ? existingScreen.cassetteSize : 'slim';

        // 1. Calculate smallest and biggest width
        const widthTop = parseInt(win.widthTop) || 0;
        const widthMiddle = parseInt(win.widthMiddle) || 0;
        const widthBottom = parseInt(win.widthBottom) || 0;
        const widths = [widthTop, widthMiddle, widthBottom].filter(w => w > 0);
        const smallestWidth = widths.length > 0 ? Math.min(...widths) : 0;
        const biggestWidth = widths.length > 0 ? Math.max(...widths) : 0;

        // 2. Calculate smallest height
        const heightLeft = parseInt(win.heightLeft) || 0;
        const heightRight = parseInt(win.heightRight) || 0;
        const heights = [heightLeft, heightRight].filter(h => h > 0);
        const smallestHeight = heights.length > 0 ? Math.min(...heights) : 0;

        const isRecess = win.mountType === 'Recess mounted' || win.mountType === 'Innvendig';
        const isWall = win.mountType === 'Wall mounted' || win.mountType === 'Utvendig';

        let finalWidth = 800;
        let finalHeight = 200;
        let cassetteSize = cassetteSizePreset;
        let mountingBracketComputed = mountingBracket;
        let fitsUnderWaterboard = false;

        if (isRecess) {
          finalWidth = smallestWidth - 4;
          finalHeight = smallestHeight - 6;
          cassetteSize = window.ScreenluxEngine.cassetteFor(finalWidth, finalHeight);
        } else if (isWall) {
          finalWidth = biggestWidth + 76;

          // For Norway Version 3 on-wall calculations
          if (isNO) {
            const waterboardDist = parseInt(win.waterboardDistance) || 0;
            const handleDist = parseInt(win.handleDistance) || 0;
            const clearance = parseInt(win.topToWaterboardHeight) || 0;
            
            // Cassette size prediction for clearance check
            const predictedCassetteNeed = finalWidth > 3000 ? 120 : 85;
            const predictedRailNeed = predictedCassetteNeed === 120 ? 45 : 30;
            const totalNeed = predictedCassetteNeed + predictedRailNeed;
            
            // Check if we need standard additions (screen sits over/above the waterboard)
            let needsStandardAdditions = true;
            const winType = win.type || "";
            const windowOpeningOut = win.openingDirection === "out" ? "Yes" : (win.openingDirection === "in" ? "No" : (win.windowOpeningOut || ""));
            const doorOpening = win.openingDirection || win.doorOpening || "";
            const hasWaterboardVal = win.hasWaterboard || "No";

            if (winType === 'Window with waterboard on top' || winType === 'Med vannbord' || winType === 'Window with top waterboard' || winType === 'med_vannbord' || winType === 'Fenster mit Wasserbrett oben') {
              if (windowOpeningOut === 'Yes') {
                needsStandardAdditions = true;
                if (clearance >= totalNeed) {
                  fitsUnderWaterboard = true;
                } else {
                  fitsUnderWaterboard = false;
                }
              } else if (windowOpeningOut === 'No') {
                needsStandardAdditions = false;
                fitsUnderWaterboard = true;
              }
            } else if (winType === 'Sliding door' || winType === 'Skyvedør' || winType === 'skyvedor' || winType === 'Schiebetür') {
              if (hasWaterboardVal === 'Yes') {
                needsStandardAdditions = false;
                fitsUnderWaterboard = true;
              }
            } else if (winType === 'Terrace door' || winType === 'Terrassedør' || winType === 'terrassedor' || winType === 'Terrassentür') {
              if (hasWaterboardVal === 'Yes') {
                if (doorOpening === 'in') {
                  needsStandardAdditions = false;
                  fitsUnderWaterboard = true;
                } else if (doorOpening === 'out') {
                  needsStandardAdditions = true;
                  if (clearance >= totalNeed) {
                    fitsUnderWaterboard = true;
                  } else {
                    fitsUnderWaterboard = false;
                  }
                }
              }
            }

            if (needsStandardAdditions) {
              const heightWithSlim = smallestHeight + 85 + 30; // 85 mm cassette + 30 mm bottom rail
              if (window.ScreenluxEngine.cassetteFor(finalWidth, heightWithSlim) === 'large') {
                cassetteSize = 'large';
                finalHeight = smallestHeight + 120 + 45; // 120 mm cassette + 45 mm bottom rail
              } else {
                cassetteSize = 'slim';
                finalHeight = heightWithSlim;
              }

              // Roof overhang adjustment for Norway
              if (win.hasRoofOverhang === "Yes") {
                const cassetteHeight = cassetteSize === "large" ? 120 : 85;
                const bottomRail = cassetteSize === "large" ? 45 : 30;
                const totalNeedOverhang = cassetteHeight + bottomRail;
                const overhangDist = parseInt(win.roofOverhangDistance) || 0;
                if (overhangDist < totalNeedOverhang) {
                  const buffer = 5;
                  const availableSpace = Math.max(0, overhangDist - buffer);
                  finalHeight = smallestHeight + availableSpace;
                }
              }
            } else {
              // Fits under waterboard
              finalHeight = smallestHeight;
              cassetteSize = window.ScreenluxEngine.cassetteFor(finalWidth, finalHeight);
            }

            // Bracket calculation for Norway
            const maxOffset = fitsUnderWaterboard ? handleDist : Math.max(waterboardDist, handleDist);
            if (maxOffset > 50) {
              mountingBracketComputed = 'flexible';
            } else if (maxOffset >= 1) {
              mountingBracketComputed = 'fixed';
            } else {
              mountingBracketComputed = 'none';
            }
          } else {
            // General / German Wall mounted calculations
            const heightWithSlim = smallestHeight + 85 + 30; // 85 mm cassette + 30 mm bottom rail
            if (window.ScreenluxEngine.cassetteFor(finalWidth, heightWithSlim) === 'large') {
              cassetteSize = 'large';
              finalHeight = smallestHeight + 120 + 45; // 120 mm cassette + 45 mm bottom rail
            } else {
              cassetteSize = 'slim';
              finalHeight = heightWithSlim;
            }

            // Roof overhang adjustment for Germany
            if (win.hasRoofOverhang === "Yes") {
              const cassetteHeight = cassetteSize === "large" ? 120 : 85;
              const bottomRail = cassetteSize === "large" ? 45 : 30;
              const totalNeed = cassetteHeight + bottomRail;
              const overhangDist = parseInt(win.roofOverhangDistance) || 0;
              if (overhangDist < totalNeed) {
                const buffer = 5;
                const availableSpace = Math.max(0, overhangDist - buffer);
                finalHeight = smallestHeight + availableSpace;
              }
            }
          }
        } else {
          // Fallback if mountType is not recess or wall (e.g. custom/unknown)
          finalWidth = smallestWidth || 800;
          finalHeight = smallestHeight || 200;
          cassetteSize = window.ScreenluxEngine.cassetteFor(finalWidth, finalHeight);
        }

        const screenName = win.name || '';

        // Run validation
        const v = window.ScreenluxEngine.validateDimensions(finalWidth, finalHeight);

        return {
          id: existingScreen ? existingScreen.id : (Date.now() + idx),
          name: screenName,
          width: finalWidth,
          height: finalHeight,
          // Preserve raw measurements for cart properties extraction
          rawMountType: win.mountType,
          rawFasteningType: win.fasteningType,
          rawType: win.type,
          rawWidthTop: win.widthTop,
          rawWidthMiddle: win.widthMiddle,
          rawWidthBottom: win.widthBottom,
          rawHeightLeft: win.heightLeft,
          rawHeightRight: win.heightRight,
          rawWindowOpeningOut: win.windowOpeningOut,
          rawTopToWaterboardHeight: win.topToWaterboardHeight,
          rawDoorOpening: win.doorOpening,
          rawHasWaterboard: win.hasWaterboard,
          rawHasHandle: win.hasHandle,
          rawWaterboardDistance: win.waterboardDistance,
          rawHandleDistance: win.handleDistance,
          rawHasRoofOverhang: win.hasRoofOverhang,
          rawRoofOverhangDistance: win.roofOverhangDistance,
          rawIsEstimate: win.isEstimate,
          fitsUnderWaterboard: fitsUnderWaterboard,
          
          frameColor: frameColor,
          fabricColor: fabricColor,
          fabricType: fabricType,
          cassetteSize: cassetteSize,
          motor: motor,
          cableExit: cableExit,
          motorPlacement: motorPlacement,
          mountingBracket: mountingBracketComputed,
          measurementGuarantee: !!win.measurementGuarantee,
          originalWidth: win.measurementGuarantee ? finalWidth : null,
          originalHeight: win.measurementGuarantee ? finalHeight : null,
          expanded: previouslyExpandedIndex >= 0 ? idx === previouslyExpandedIndex : idx === 0,
          valid: v.valid,
          errors: v.valid ? {} : { dimension: v.error }
        };
      });

      // Clear the local storage keys for transfer
      localStorage.removeItem('screenlux_transfer_measurements');
      localStorage.removeItem('screenlux_estimate_transfer');
      // Set showBaselineWizard to false
      this.state.showBaselineWizard = false;
      this.state.tempImportedWindows = null;

      // Save the hash of the imported windows list
      const hash = this.computeWindowsHash(windows);
      if (hash) {
        localStorage.setItem('screenlux_last_imported_measurements_hash', hash);
      }

      // Render configurator
      this.render();

      // Dispatch event to sync SVG configurator with the open screen's choices
      if (this.state.screens.length > 0) {
        this.updateSVGConfigurator(this.getActiveScreen());
      }

      // Update gallery image to the first screen's options
      this.jumpToFirstSlideWithImage();

    } catch (err) {
      console.error('Error confirming baseline wizard:', err);
      alert('Error: ' + err.message);
    }
  }

  /* --- Rendering --- */

  saveFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return null;
    // Simple focus preservation strategy
    if (active.dataset.field) {
      // Return enough info to find it again essentially
      const allInputs = Array.from(this.querySelectorAll(`input[data-field="${active.dataset.field}"]`));
      const index = allInputs.indexOf(active);
      return { type: 'dimension', field: active.dataset.field, index };
    }
    return null;
  }

  restoreFocus(saved) {
    if (!saved) return;
    try {
      if (saved.type === 'dimension') {
        const allInputs = Array.from(this.querySelectorAll(`input[data-field="${saved.field}"]`));
        if (allInputs[saved.index]) {
          const el = allInputs[saved.index];
          el.focus();
          // Restore cursor position for text/number inputs
          if (el.type === 'text' || el.type === 'number') {
            const len = el.value.length;
            el.setSelectionRange(len, len);
          }
        }
      }
    } catch (e) {
      // Ignore focus errors
    }
  }

  updatePageHeader(showWizard, texts) {
    const titleEl = document.querySelector('.product-title');
    const badgeEl = document.querySelector('.de-badge-container');
    
    // Manage dynamic description
    let descEl = document.querySelector('.configurator-page-description');
    if (!descEl) {
      descEl = document.createElement('div');
      descEl.className = 'configurator-page-description';
      descEl.style.margin = '16px 0 24px 0';
      descEl.style.fontSize = '15px';
      descEl.style.color = 'var(--sl-text-secondary)';
      descEl.style.lineHeight = '1.6';
      
      const titleSection = document.querySelector('.product-title-section');
      if (titleSection) {
        if (badgeEl) {
          badgeEl.parentNode.insertBefore(descEl, badgeEl.nextSibling);
        } else {
          titleSection.parentNode.insertBefore(descEl, titleSection.nextSibling);
        }
      }
    }

    if (showWizard && texts) {
      if (titleEl) {
        if (!this._originalTitle) this._originalTitle = titleEl.textContent;
        titleEl.textContent = texts.wizardTitle;
      }
      descEl.textContent = texts.wizardSubtitle;
      descEl.style.display = 'block';
      if (badgeEl) badgeEl.style.display = 'flex';
    } else {
      if (titleEl && this._originalTitle) {
        titleEl.textContent = this._originalTitle;
        this._originalTitle = null;
      }
      descEl.style.display = 'none';
      if (badgeEl) badgeEl.style.display = 'flex';
    }
  }

  render() {
    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const wizardLocales = {
      de: {
        wizardTitle: 'Basiskonfiguration wählen',
        wizardSubtitle: 'Wähle die Standardoptionen für deine ausgemessenen Fenster. Diese Optionen gelten für alle Screens, können aber später individuell angepasst werden.',
        confirmBtn: 'Weiter'
      },
      no: {
        wizardTitle: 'Velg basiskonfigurasjon',
        wizardSubtitle: 'Velg standardalternativer for dine målte vinduer. Disse alternativene vil gjelde for alle screens, men kan tilpasses individuelt senere.',
        confirmBtn: 'Fortsett'
      },
      nb: {
        wizardTitle: 'Velg basiskonfigurasjon',
        wizardSubtitle: 'Velg standardalternativer for dine målte vinduer. Disse alternativene vil gjelde for alle screens, men kan tilpasses individuelt senere.',
        confirmBtn: 'Fortsett'
      },
      en: {
        wizardTitle: 'Choose base configuration',
        wizardSubtitle: 'Choose the default options for your measured windows. These options will apply to all screens, but can be customized individually later.',
        confirmBtn: 'Continue'
      }
    };
    const texts = wizardLocales[localeCode] || wizardLocales.en;

    this.updatePageHeader(this.state.showBaselineWizard, texts);

    if (this.state.showBaselineWizard) {
      document.body.classList.add('baseline-wizard-active');
      this.replaceChildren(this.renderBaselineWizard(texts));
      return;
    } else {
      document.body.classList.remove('baseline-wizard-active');
    }
    const savedFocus = this.saveFocus();

    // Build off-DOM
    const container = document.createElement('div');
    container.className = 'configurator-app';

    // 1. Screens Group
    const screensGroup = document.createElement('div');
    screensGroup.className = 'configurator-group-box';

    const screensContainer = document.createElement('div');
    screensContainer.className = 'screens-list';

    // Add Header for Screens Section
    let headerHTML = `<label class="grouping-title">${window.ScreenluxTranslations.title}</label>`;
    if (this.state.dbSyncStatus && this.state.dbSyncStatus !== 'no_measurements') {
      let statusText = '';
      let iconHtml = '';

      if (this.state.dbSyncStatus === 'loading') {
        statusText = localeCode === 'no' || localeCode === 'nb' ? 'Laster lagrede vinduer...' : (localeCode === 'de' ? 'Gespeicherte Fenster werden geladen...' : 'Loading saved windows...');
        iconHtml = `
          <svg class="spinner-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b06000" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; animation: spin-db-sync 1.5s linear infinite;">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
          <style>
            @keyframes spin-db-sync {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
      } else if (this.state.dbSyncStatus === 'success') {
        statusText = localeCode === 'no' || localeCode === 'nb' ? 'Lagrede vinduer ble lastet inn!' : (localeCode === 'de' ? 'Gespeicherte Fenster erfolgreich geladen!' : 'Saved windows loaded successfully!');
        iconHtml = `
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; margin-top: 1px;">
            <path d="M10 3L4.5 8.5L2 6" stroke="#137333" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (this.state.dbSyncStatus === 'no_measurements') {
        statusText = localeCode === 'no' || localeCode === 'nb' ? 'Fant ingen lagrede vinduer for denne kunden.' : (localeCode === 'de' ? 'Keine gespeicherten Fenster für diesen Kunden gefunden.' : 'No saved windows found for this customer.');
        iconHtml = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        `;
      } else if (this.state.dbSyncStatus === 'error') {
        statusText = localeCode === 'no' || localeCode === 'nb' ? `Feil under innlasting: ${this.state.dbSyncError || 'Ukjent feil'}` : (localeCode === 'de' ? `Fehler beim Laden: ${this.state.dbSyncError || 'Unbekannter Fehler'}` : `Error loading measurements: ${this.state.dbSyncError || 'Unknown error'}`);
        iconHtml = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c5221f" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        `;
      }
      
      headerHTML += `
        <div class="db-sync-banner db-sync-${this.state.dbSyncStatus}" style="
          margin: 12px 0;
          padding: 10px 16px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          background-color: ${this.state.dbSyncStatus === 'success' ? '#e6f4ea' : (this.state.dbSyncStatus === 'error' ? '#fce8e6' : (this.state.dbSyncStatus === 'loading' ? '#fef7e0' : '#e8f0fe'))};
          color: ${this.state.dbSyncStatus === 'success' ? '#137333' : (this.state.dbSyncStatus === 'error' ? '#c5221f' : (this.state.dbSyncStatus === 'loading' ? '#b06000' : '#1a73e8'))};
          border: 1px solid ${this.state.dbSyncStatus === 'success' ? '#ceead6' : (this.state.dbSyncStatus === 'error' ? '#fad2cf' : (this.state.dbSyncStatus === 'loading' ? '#ffe0b2' : '#d2e3fc'))};
        ">
          ${iconHtml}
          <span>${statusText}</span>
        </div>
      `;
    }
    screensContainer.innerHTML = headerHTML;

    this.state.screens.forEach((screen, index) => {
      screensContainer.appendChild(this.renderScreenItem(screen, index));
    });
    screensGroup.appendChild(screensContainer);

    // 2. Add Screen Button
    const addBtn = document.createElement('button');
    addBtn.className = 'btn btn-dashed margin-top-sm';
    addBtn.innerHTML = `<span>+</span> ${window.ScreenluxTranslations.addAnotherOne}`;
    addBtn.onclick = this.handleAddScreen;
    screensGroup.appendChild(addBtn);

    // 3. Global Solar Check (Upsell) - MOVED TO INSTALLATION SECTION

    // Add Screens Group to Main Container
    container.appendChild(screensGroup);

    // 4. Installation Section
    container.appendChild(this.renderInstallationSection());

    // 5. Brackets Section removed

    // 6. Steuerung Section
    container.appendChild(this.renderExtraSection('steuerung', window.ScreenluxTranslations.steuerungTitle, window.ScreenluxTranslations.noSteuerung));

    // 5.1 Automatisierung Section
    container.appendChild(this.renderExtraSection('automatisierung', window.ScreenluxTranslations.automatisierungTitle, window.ScreenluxTranslations.noAutomatisierung));

    // 6. Order Summary & Cart (includes the award quote + feature bullets)
    container.appendChild(this.renderOrderSummary());

    // 7. Sticky Bar
    container.appendChild(this.renderStickyBar());

    // 9. Guarantee Details Modal Overlay
    if (this.state.showGuaranteeDetailsModal) {
      container.appendChild(this.renderGuaranteeDetailsModal());
    }

    // 10. Calculation Details Modal Overlay
    if (this.state.showCalculationModalIndex !== null && this.state.showCalculationModalIndex !== undefined) {
      container.appendChild(this.renderCalculationModal(this.state.showCalculationModalIndex));
    }

    // Atomic Swap
    this.replaceChildren(container);

    // Update sticky bar state immediately
    if (this.updateStickyBar) this.updateStickyBar();

    // Restore Focus
    this.restoreFocus(savedFocus);

    // Save state to storage
    this.saveStateToStorage();
  }

  renderGuaranteeDetailsModal() {
    document.body.style.overflow = 'hidden';
    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const isDE = localeCode === 'de';
    const isNO = localeCode === 'no' || localeCode === 'nb';

    const modalTitle = isDE ? '100% Passgarantie Details' : (isNO ? '100% Målegaranti detaljer' : '100% Fit Guarantee Details');
    
    const firstTerm = isDE
      ? 'Solltest du dich vermessen haben, während die 100% Passgarantie aktiv ist, passen wir deine Screens kostenlos an oder ersetzen sie.'
      : (isNO
        ? 'Dersom du måler feil når 100% målegarantien er aktiv, vil vi tilpasse eller erstatte dine screens uten ekstra kostnad.'
        : 'If you measure incorrectly while the 100% Fit Guarantee is active, we will replace or repair the screens free of charge.');

    const otherTerms = isDE ? [
      'Die 100% Passgarantie gilt für maximal 5 Zip-Screens pro Kunde. Jeder Zip-Screen kann nur einmalig ersetzt bzw. angepasst werden.',
      'Abweichungen von mehr als 5 cm (50 mm) sind von der 100% Passgarantie ausgeschlossen, da dies als grober Messfehler und nicht als normale Messtoleranz eingestuft wird.',
      'Wir behalten uns das Recht vor, dich telefonisch zu kontaktieren, wenn wir weitere Details zu deinem Projekt benötigen.',
      'Sollten wir eine Überprüfung für notwendig halten, setzen wir die Produktion zu deiner Sicherheit aus, bis wir die Maße mit dir abgestimmt haben. Wir kontaktieren dich sowohl per Telefon als auch per E-Mail, um dich bestmöglich zu erreichen.'
    ] : (isNO ? [
      'Målegarantien gjelder for maksimalt 5 Zip-screens per kunde. Hver Zip-screen kan kun erstattes eller tilpasses én gang.',
      'Dersom målene avviker med mer enn 5 cm (50 mm), faller dette utenfor målegarantien, da dette anses som en grov feilmåling og ikke en vanlig marginfeil.',
      'Vi forbeholder oss retten til å ringe deg dersom vi trenger mer informasjon om ditt prosjekt.',
      'Dersom vi anser en sjekk som nødvendig, vil vi for din sikkerhet sette produksjonen på vent til vi har bekreftet målene med deg. Vi vil kontakte deg via både telefon og e-post.'
    ] : [
      'The guarantee applies to a maximum of 5 Zip-screens per customer. Each Zip-screen can only be replaced or repaired once.',
      'If the measurements deviate by more than 5 cm (50 mm), it is outside the scope of the guarantee, as this is considered a significant mistake rather than a measuring tolerance.',
      'We are allowed to call you if we need more information about your project.',
      'If we believe a double-check is necessary, we will put production on hold for your safety until we have confirmed the measurements with you. We will contact you via both phone and email.'
    ]);

    const closeText = isDE ? 'Schließen' : (isNO ? 'Lukk' : 'Close');

    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'sl-modal-overlay';
    
    modalOverlay.innerHTML = `
      <div class="sl-modal-container" style="max-width: 550px;">
        <button type="button" class="sl-modal-close" aria-label="${closeText}">
          <span class="sl-close-icon">&times;</span>
        </button>
        <div class="sl-modal-content">
          <h3 class="sl-modal-heading" style="display: flex; align-items: center; gap: 8px; font-size: 1.8rem; margin: 0; font-weight: 700;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#137333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 11 2 2 4-4"/>
            </svg>
            <span>${modalTitle}</span>
          </h3>
          <div style="margin-top: 1.5rem; font-size: 1.3rem; line-height: 1.6; color: var(--sl-text-primary); max-height: 60vh; overflow-y: auto; padding-right: 8px;">
            <p style="font-size: 1.5rem; font-weight: 600; color: var(--sl-text-primary); margin: 0 0 1.5rem 0; line-height: 1.4;">${firstTerm}</p>
            <ul style="margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 16px; list-style-type: disc;">
              ${otherTerms.map(t => `<li style="margin-bottom: 0;">${t}</li>`).join('')}
            </ul>
          </div>
          <button class="sl-btn-premium close-footer-btn" style="margin-top: 2rem;">
            ${closeText}
          </button>
        </div>
      </div>
    `;

    const closeHandler = () => {
      this.state.showGuaranteeDetailsModal = false;
      document.body.style.overflow = '';
      this.render();
    };

    modalOverlay.querySelector('.sl-modal-close').onclick = closeHandler;
    modalOverlay.querySelector('.close-footer-btn').onclick = closeHandler;
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        closeHandler();
      }
    };

    return modalOverlay;
  }

  showMeasureAssistantModal(linkUrl) {
    document.body.style.overflow = 'hidden';
    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const isDE = localeCode === 'de';
    const isNO = localeCode === 'no' || localeCode === 'nb';

    const modalTitle = isDE ? 'Aufmaß-Assistent auf dem Smartphone starten' : (isNO ? 'Start måleguide på mobilen' : 'Start Measuring Assistant on Mobile');
    const modalSubtitle = isDE 
      ? 'Scanne den QR-Code mit deiner Smartphone-Kamera, um deine Fenster einfach auszumessen. Damit aktivierst du deine 100% Passgarantie – falls du dich vermisst, produzieren wir kostenlos neu.'
      : (isNO 
          ? 'Skann QR-koden med mobilkameraet for å måle vinduene dine. Dette aktiverer din 100% målegaranti – hvis du måler feil, produserer vi en ny helt gratis.' 
          : 'Scan the QR code with your smartphone camera to measure your windows. This activates your 100% Fit Guarantee – if you measure wrong, we will produce a new one for free.');
    
    const fallbackText = isDE ? 'Oder direkt auf diesem Gerät messen →' : (isNO ? 'Eller mål direkte på denne enheten →' : 'Or measure directly on this device →');
    const closeText = isDE ? 'Schließen' : (isNO ? 'Lukk' : 'Close');

    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || this.dataset.customerId;
    const isLoggedIn = !!customerId;

    const returnPath = customerId ? `/pages/measure-app?customer_id=${customerId}` : '/pages/measure-app';
    const loginUrl = `/customer_authentication/login?return_to=${encodeURIComponent(returnPath)}`;

    const tipHtml = isLoggedIn ? '' : (
      isDE 
        ? `<div style="margin-top: 1.5rem; margin-bottom: 1.5rem; padding: 12px; background-color: #FFF9E6; border: 1px solid #FFE0B2; border-radius: 8px; font-size: 1.3rem; color: #B78103; text-align: left; line-height: 1.4;">
            <strong>Tipp:</strong> Melde dich am besten <a href="${loginUrl}" style="color: #4F46E5; text-decoration: underline; font-weight: 600;">zuerst hier an</a>, damit deine gemessenen Maße direkt live auf diesem Bildschirm angezeigt werden.
           </div>`
        : (isNO 
            ? `<div style="margin-top: 1.5rem; margin-bottom: 1.5rem; padding: 12px; background-color: #FFF9E6; border: 1px solid #FFE0B2; border-radius: 8px; font-size: 1.3rem; color: #B78103; text-align: left; line-height: 1.4;">
                <strong>Tips:</strong> Logg deg gjerne <a href="${loginUrl}" style="color: #4F46E5; text-decoration: underline; font-weight: 600;">inn her først</a>, slik at dine mål vises direkte på denne skjermen live.
               </div>`
            : `<div style="margin-top: 1.5rem; margin-bottom: 1.5rem; padding: 12px; background-color: #FFF9E6; border: 1px solid #FFE0B2; border-radius: 8px; font-size: 1.3rem; color: #B78103; text-align: left; line-height: 1.4;">
                <strong>Tip:</strong> We recommend <a href="${loginUrl}" style="color: #4F46E5; text-decoration: underline; font-weight: 600;">logging in here first</a> so that your measured windows display live on this screen.
               </div>`
          )
    );

    const absoluteUrl = window.location.origin + (linkUrl || '/pages/measure-app');
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(absoluteUrl)}`;

    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'sl-modal-overlay';
    
    modalOverlay.innerHTML = `
      <div class="sl-modal-container" style="max-width: 450px;">
        <button type="button" class="sl-modal-close" aria-label="${closeText}">
          <span class="sl-close-icon">&times;</span>
        </button>
        <div class="sl-modal-content" style="text-align: center;">
          <h3 class="sl-modal-heading" style="text-align: left; margin-bottom: 0.8rem; font-size: 2.2rem; font-weight: 700; color: #111827; line-height: 1.3;">${modalTitle}</h3>
          <p class="sl-modal-sub" style="text-align: left; font-size: 1.45rem; line-height: 1.6; margin-bottom: 2.2rem; color: #4b5563;">${modalSubtitle}</p>
          ${tipHtml}
          
          <div style="background: #ffffff; padding: 12px; border: 1px solid rgba(0,0,0,0.08); border-radius: 12px; display: inline-flex; justify-content: center; align-items: center; margin: 8px auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
            <img src="${qrImageUrl}" alt="QR Code" width="200" height="200" style="display: block;" />
          </div>

          <div style="margin-top: 2rem;">
            <a href="${linkUrl || '/pages/measure-app'}" target="_blank" class="fallback-link" style="font-size: 1.45rem; color: #4F46E5; font-weight: 600; text-decoration: underline;">
              ${fallbackText}
            </a>
          </div>

          <button class="sl-btn-premium close-footer-btn" style="margin-top: 2.5rem; width: 100%; justify-content: center; padding: 1.4rem 2.4rem; font-size: 1.5rem; font-weight: 600; border-radius: 8px;">
            ${closeText}
          </button>
        </div>
      </div>
    `;

    const closeHandler = () => {
      modalOverlay.remove();
      document.body.style.overflow = '';
      this.fetchAndApplyDatabaseMeasurements();
    };

    modalOverlay.querySelector('.sl-modal-close').onclick = closeHandler;
    modalOverlay.querySelector('.close-footer-btn').onclick = closeHandler;
    modalOverlay.querySelector('.fallback-link').onclick = closeHandler;
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        closeHandler();
      }
    };

    document.body.appendChild(modalOverlay);
  }

  renderCalculationModal(index) {
    const screen = this.state.screens[index];
    if (!screen) return document.createElement('div');

    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const isDE = localeCode === 'de';
    const isNO = localeCode === 'no' || localeCode === 'nb';
    let needsStandardAdditions = true;
    let fitsUnderWaterboard = false;

    const rawWidths = [parseInt(screen.rawWidthTop) || 0, parseInt(screen.rawWidthMiddle) || 0, parseInt(screen.rawWidthBottom) || 0].filter(w => w > 0);
    const rawHeights = [parseInt(screen.rawHeightLeft) || 0, parseInt(screen.rawHeightRight) || 0].filter(h => h > 0);
    const minWidth = rawWidths.length > 0 ? Math.min(...rawWidths) : 0;
    const maxWidth = rawWidths.length > 0 ? Math.max(...rawWidths) : 0;
    const minHeight = rawHeights.length > 0 ? Math.min(...rawHeights) : 0;

    const modalTitle = isDE ? 'Berechnungsdetails' : (isNO ? 'Utregningsdetaljer' : 'Calculation Details');
    const closeText = isDE ? 'Schließen' : (isNO ? 'Lukk' : 'Close');

    // 1. Determine Mount Type text
    let mountTypeText = screen.rawMountType || 'Unknown';
    if (screen.rawMountType === 'Recess mounted' || screen.rawMountType === 'Innvendig') {
      mountTypeText = isDE ? 'In der Laibung (Innvendig)' : (isNO ? 'Innvendig i nisje' : 'Recess mounted');
    } else if (screen.rawMountType === 'Wall mounted' || screen.rawMountType === 'Utvendig') {
      mountTypeText = isDE ? 'An die Wand (Utvendig)' : (isNO ? 'Utvendig på vegg' : 'Wall mounted');
    }

    // 2. Determine Window Type text
    let typeText = screen.rawType || 'Unknown';
    const rawWinType = screen.rawType || '';
    if (rawWinType === 'Window without waterboard on top' || rawWinType === 'uten_vannbrett' || rawWinType === 'uten_ramme') {
      typeText = isDE ? 'Fenster ohne Wasserbrett oben' : (isNO ? 'Vindu uten vannbrett på topp' : 'Window without waterboard on top');
    } else if (rawWinType === 'Window with waterboard on top' || rawWinType === 'med_vannbord' || rawWinType === 'Window with top waterboard') {
      typeText = isDE ? 'Fenster mit Wasserbrett oben' : (isNO ? 'Vindu med vannbrett på topp' : 'Window with waterboard on top');
    } else if (rawWinType === 'Sliding door' || rawWinType === 'skyvedor' || rawWinType === 'Skyvedør') {
      typeText = isDE ? 'Schiebetür' : (isNO ? 'Skyvedør' : 'Sliding door');
    } else if (rawWinType === 'Terrace door' || rawWinType === 'terrassedor' || rawWinType === 'Terrassedør') {
      typeText = isDE ? 'Terrassentür' : (isNO ? 'Terrassedør' : 'Terrace door');
    }

    // 3. Step-by-step logic
    const steps = [];

    // Step 1: Raw inputs summary
    const step1Title = isDE ? '1. Gemessene Ausgangswerte' : (isNO ? '1. Målte utgangsverdier' : '1. Measured input values');
    let step1Desc = '';
    if (isDE) {
      step1Desc = `Gemessene Breiten: ${rawWidths.join(', ')} mm (min: ${minWidth} mm, max: ${maxWidth} mm).<br>` +
                  `Gemessene Höhen: ${rawHeights.join(', ')} mm (min: ${minHeight} mm).`;
    } else if (isNO) {
      step1Desc = `Målte bredder: ${rawWidths.join(', ')} mm (minste: ${minWidth} mm, største: ${maxWidth} mm).<br>` +
                  `Målte høyder: ${rawHeights.join(', ')} mm (minste: ${minHeight} mm).`;
    } else {
      step1Desc = `Measured widths: ${rawWidths.join(', ')} mm (min: ${minWidth} mm, max: ${maxWidth} mm).<br>` +
                  `Measured heights: ${rawHeights.join(', ')} mm (min: ${minHeight} mm).`;
    }
    steps.push({ title: step1Title, desc: step1Desc });

    // Step 2: Width calculation
    const step2Title = isDE ? '2. Breitenberechnung' : (isNO ? '2. Utregning av bredde' : '2. Width calculation');
    let step2Desc = '';
    if (screen.rawMountType === 'Recess mounted' || screen.rawMountType === 'Innvendig') {
      if (isDE) {
        step2Desc = `Laibungsmontage: Kleinste gemessene Breite (${minWidth} mm) minus 4 mm Fehlermarge = <strong>${screen.width} mm</strong>.`;
      } else if (isNO) {
        step2Desc = `Innvendig montering i nisje: Minste målte bredde (${minWidth} mm) minus 4 mm feilmargin = <strong>${screen.width} mm</strong>.`;
      } else {
        step2Desc = `Recess mounted: Smallest measured width (${minWidth} mm) minus 4 mm error margin = <strong>${screen.width} mm</strong>.`;
      }
    } else {
      // Wall mounted
      if (isDE) {
        step2Desc = `Wandmontage (Wall): Größte gemessene Breite (${maxWidth} mm) plus 76 mm (für zwei 38 mm Führungsschienen) = <strong>${screen.width} mm</strong>.`;
      } else if (isNO) {
        step2Desc = `Utvendig montering på vegg: Største målte bredde (${maxWidth} mm) pluss 76 mm (for to 38 mm sideskinner) = <strong>${screen.width} mm</strong>.`;
      } else {
        step2Desc = `Wall mounted: Largest measured width (${maxWidth} mm) plus 76 mm (for two 38 mm side rails) = <strong>${screen.width} mm</strong>.`;
      }
    }
    steps.push({ title: step2Title, desc: step2Desc });

    // Step 3: Height calculation
    const step3Title = isDE ? '3. Höhenberechnung' : (isNO ? '3. Utregning av høyde' : '3. Height calculation');
    let step3Desc = '';
    if (screen.rawMountType === 'Recess mounted' || screen.rawMountType === 'Innvendig') {
      if (isDE) {
        step3Desc = `Laibungsmontage: Kleinste gemessene Höhe (${minHeight} mm) minus 6 mm Fehlermarge = <strong>${screen.height} mm</strong>.`;
      } else if (isNO) {
        step3Desc = `Innvendig montering i nisje: Minste målte høyde (${minHeight} mm) minus 6 mm feilmargin = <strong>${screen.height} mm</strong>.`;
      } else {
        step3Desc = `Recess mounted: Smallest measured height (${minHeight} mm) minus 6 mm error margin = <strong>${screen.height} mm</strong>.`;
      }
    } else {
      // Wall mounted
      if (isNO) {
        const waterboardDist = parseInt(screen.rawWaterboardDistance) || 0;
        const handleDist = parseInt(screen.rawHandleDistance) || 0;
        const clearance = parseInt(screen.rawTopToWaterboardHeight) || 0;
        
        // Cassette and rail size prediction for clearance check
        const predictedCassetteNeed = screen.width > 3000 ? 120 : 85;
        const predictedRailNeed = predictedCassetteNeed === 120 ? 45 : 30;
        const totalNeed = predictedCassetteNeed + predictedRailNeed;

        needsStandardAdditions = true;
        let fitReason = '';
        const winType = screen.rawType || "";
        const windowOpeningOut = screen.rawOpeningDirection === "out" ? "Yes" : (screen.rawOpeningDirection === "in" ? "No" : (screen.rawWindowOpeningOut || ""));
        const doorOpening = screen.rawOpeningDirection || screen.rawDoorOpening || "";
        const hasWaterboardVal = screen.rawHasWaterboard || "No";

        if (winType === 'Window with waterboard on top' || winType === 'Med vannbord' || winType === 'Window with top waterboard' || winType === 'med_vannbord' || winType === 'Fenster mit Wasserbrett oben') {
          if (windowOpeningOut === 'Yes') {
            needsStandardAdditions = true;
            if (clearance >= totalNeed) {
              fitsUnderWaterboard = true;
              fitReason = `Vinduet åpner utover. Det er plass til kassetten over vinduet siden avstand til vannbrett er ${clearance} mm som er mer enn kassett (${predictedCassetteNeed} mm) + bunnskinne (${predictedRailNeed} mm) = ${totalNeed} mm. Screenen monteres under vannbrettet.`;
            } else {
              fitsUnderWaterboard = false;
              fitReason = `Vinduet åpner utover, men det er ikke nok plass til kassetten over vindusrammen (${clearance} mm tilgjengelig < ${totalNeed} mm kassetthøyde + bunnskinne). Kassetten må monteres på veggen over vannbrettet.`;
            }
          } else if (windowOpeningOut === 'No') {
            needsStandardAdditions = false;
            fitsUnderWaterboard = true;
            fitReason = `Vinduet åpner innover eller er fast. Kassetten monteres under vannbrettet.`;
          }
        } else if (winType === 'Sliding door' || winType === 'Skyvedør' || winType === 'skyvedor' || winType === 'Schiebetür') {
          if (hasWaterboardVal === 'Yes') {
            needsStandardAdditions = false;
            fitsUnderWaterboard = true;
            fitReason = `Skyvedør har vannbrett. Kassetten monteres under vannbrettet.`;
          } else {
            fitReason = `Skyvedør har ikke vannbrett. Standard veggmonteringstillegg gjelder.`;
          }
        } else if (winType === 'Terrace door' || winType === 'Terrassedør' || winType === 'terrassedor' || winType === 'Terrassentür') {
          if (hasWaterboardVal === 'Yes') {
            if (doorOpening === 'in') {
              needsStandardAdditions = false;
              fitsUnderWaterboard = true;
              fitReason = `Terrassedør åpner innover. Kassetten monteres under vannbrettet.`;
            } else if (doorOpening === 'out') {
              needsStandardAdditions = true;
              if (clearance >= totalNeed) {
                fitsUnderWaterboard = true;
                fitReason = `Terrassedør åpner utover. Det er plass til kassetten over dørkarmen siden avstand til vannbrett er ${clearance} mm som er mer enn kassett (${predictedCassetteNeed} mm) + bunnskinne (${predictedRailNeed} mm) = ${totalNeed} mm. Screenen monteres under vannbrettet.`;
              } else {
                fitsUnderWaterboard = false;
                fitReason = `Terrassedør åpner utover, men det er ikke nok plass til kassetten over dørkarmen (${clearance} mm tilgjengelig < ${totalNeed} mm kassetthøyde + bunnskinne). Kassetten må monteres på veggen over vannbrettet.`;
              }
            }
          } else {
            fitReason = `Terrassedør har ikke vannbrett. Standard veggmonteringstillegg gjelder.`;
          }
        } else {
          fitReason = `Standard veggmonteringstillegg gjelder for denne vindustypen.`;
        }

        if (needsStandardAdditions) {
          const cassetteAdd = screen.cassetteSize === 'large' ? 120 : 85;
          const railAdd = screen.cassetteSize === 'large' ? 45 : 30;
          const dist = parseInt(screen.rawRoofOverhangDistance) || 0;
          const totalNeedOverhang = cassetteAdd + railAdd;
          
          if (screen.rawHasRoofOverhang === 'Yes' && dist < totalNeedOverhang) {
            const buffer = 5;
            const space = Math.max(0, dist - buffer);
            const intrusion = totalNeedOverhang - space;
            const isWindowOpeningOut = (screen.rawWindowOpeningOut === 'Yes' || screen.rawDoorOpening === 'out');
            let warnText = '';
            if (isWindowOpeningOut && intrusion > 0) {
              warnText = `<br><span style="color: #d93838; font-weight: bold;">⚠️ Advarsel: Siden vinduet/døren åpner utover, vil kassetten/skinnen hindre at vinduet kan åpnes fullt ut (de stikker ${intrusion} mm ned i lysåpningen).</span>`;
            }
            step3Desc = `${fitReason}<br>Takutstikk hindring: Avstand til takutstikk er ${dist} mm, som er mindre enn kassetten og bunnskinnen til sammen (${cassetteAdd} mm + ${railAdd} mm = ${totalNeedOverhang} mm). Kassetten monteres flush mot takutstikket (med ${buffer} mm buffer). Kassetten og skinnen vil stikke ${intrusion} mm ned i vindusåpningen.${warnText}<br>Utregning: Minste målte høyde (${minHeight} mm) + takutstikk-avstand (${dist} mm) - buffer (${buffer} mm) = <strong>${screen.height} mm</strong>.`;
          } else {
            step3Desc = `${fitReason}<br>Utregning: Minste målte høyde (${minHeight} mm) + kassett (${cassetteAdd} mm) + bunnskinnetillegg (${railAdd} mm) = <strong>${screen.height} mm</strong>.`;
          }
        } else {
          step3Desc = `${fitReason}<br>Utregning: Montering under vannbrettet. Endelig høyde er lik minste målte høyde = <strong>${screen.height} mm</strong>.`;
        }
      } else {
        // Germany / General Wall mounted
        const cassetteAdd = screen.cassetteSize === 'large' ? 120 : 85;
        const railAdd = screen.cassetteSize === 'large' ? 45 : 30;
        const dist = parseInt(screen.rawRoofOverhangDistance) || 0;
        const totalNeed = cassetteAdd + railAdd;
        
        if (screen.rawHasRoofOverhang === 'Yes' && dist < totalNeed) {
          const buffer = 5;
          const space = Math.max(0, dist - buffer);
          const intrusion = totalNeed - space;
          const isWindowOpeningOut = (screen.rawWindowOpeningOut === 'Yes' || screen.rawDoorOpening === 'out');
          let warnText = '';
          if (isWindowOpeningOut && intrusion > 0) {
            if (isDE) {
              warnText = `<br><span style="color: #d93838; font-weight: bold;">⚠️ Warnung: Da das Fenster/die Tür nach außen öffnet, verhindert die Kassette/Schiene das vollständige Öffnen (sie ragen ${intrusion} mm in die Lichtöffnung hinein).</span>`;
            } else {
              warnText = `<br><span style="color: #d93838; font-weight: bold;">⚠️ Warning: Since the window/door opens outwards, the cassette/rail will prevent it from opening fully (they protrude ${intrusion} mm into the light opening).</span>`;
            }
          }
          if (isDE) {
            step3Desc = `Wandmontage mit Dachüberstand: Abstand zum Dachüberstand beträgt ${dist} mm, was kleiner ist als die Kassette und Bodenschiene zusammen (${cassetteAdd} mm + ${railAdd} mm = ${totalNeed} mm). Die Kassette wird bündig unter dem Dachüberstand montiert (mit ${buffer} mm Puffer). Kassette und Schiene ragen ${intrusion} mm in die Lichtöffnung hinein.${warnText}<br>Berechnung: Kleinste gemessene Höhe (${minHeight} mm) + Dachüberstand-Abstand (${dist} mm) - Puffer (${buffer} mm) = <strong>${screen.height} mm</strong>.`;
          } else {
            step3Desc = `Wall mounted with roof overhang: Distance to roof overhang is ${dist} mm, which is less than the cassette and bottom rail combined (${cassetteAdd} mm + ${railAdd} mm = ${totalNeed} mm). The cassette is mounted flush under the roof overhang (with ${buffer} mm buffer). Cassette and rail protrude ${intrusion} mm into the light opening.${warnText}<br>Calculation: Smallest measured height (${minHeight} mm) + roof overhang distance (${dist} mm) - buffer (${buffer} mm) = <strong>${screen.height} mm</strong>.`;
          }
        } else {
          if (isDE) {
            step3Desc = `Wandmontage (Wall): Kleinste gemessene Höhe (${minHeight} mm) + Kassette (${cassetteAdd} mm) + ${railAdd} mm Bodenschienen-Zuschlag = <strong>${screen.height} mm</strong>.`;
          } else {
            step3Desc = `Wall mounted: Smallest measured height (${minHeight} mm) + cassette (${cassetteAdd} mm) + ${railAdd} mm bottom rail allowance = <strong>${screen.height} mm</strong>.`;
          }
        }
      }
    }
    steps.push({ title: step3Title, desc: step3Desc });

    // Step 4: Cassette size selection
    const step4Title = isDE ? '4. Kassetten-Größe' : (isNO ? '4. Kassettestørrelse' : '4. Cassette size selection');
    let step4Desc = '';
    const sizeName = screen.cassetteSize === 'large' ? (isDE ? 'Groß (120x120 mm)' : (isNO ? 'Stor (120x120 mm)' : 'Large (120x120 mm)')) : (isDE ? 'Slim (85x85 mm)' : (isNO ? 'Slank (85x85 mm)' : 'Slim (85x85 mm)'));
    if (isDE) {
      step4Desc = `Eine große Kassette (Standard 120) wird erst ab einer Breite von 3000 mm oder einer Höhe von 2100 mm benötigt.<br>` +
                  `Ausgewählt: <strong>${sizeName}</strong> basierend auf Breite ${screen.width} mm und Höhe ${screen.height} mm.`;
    } else if (isNO) {
      step4Desc = `Stor kassett (Standard 120) kreves kun dersom bredden overskrider 3000 mm eller høyden overskrider 2100 mm.<br>` +
                  `Valgt: <strong>${sizeName}</strong> basert på bredde ${screen.width} mm og høyde ${screen.height} mm.`;
    } else {
      step4Desc = `A large cassette (Standard 120) is only required if the width exceeds 3000 mm or the height exceeds 2100 mm.<br>` +
                  `Selected: <strong>${sizeName}</strong> based on width ${screen.width} mm and height ${screen.height} mm.`;
    }
    steps.push({ title: step4Title, desc: step4Desc });

    // Step 5: Mounting brackets (Norway only)
    if (isNO && (screen.rawWaterboardDistance || screen.rawHandleDistance)) {
      const step5Title = '5. Monteringsbraketter';
      const waterboardDist = parseInt(screen.rawWaterboardDistance) || 0;
      const handleDist = parseInt(screen.rawHandleDistance) || 0;
      const maxOffset = fitsUnderWaterboard ? handleDist : Math.max(waterboardDist, handleDist);
      
      let bracketName = 'Ingen monteringsbraketter';
      let bracketRule = 'Ingen utstikk over 0 mm er oppgitt.';
      const waterboardText = fitsUnderWaterboard ? `${waterboardDist} mm (ignorert under vannbrett)` : `${waterboardDist} mm`;
      if (screen.mountingBracket === 'fixed') {
        bracketName = 'Faste braketter (L-braketter)';
        bracketRule = `Maksimalt utstikk er ${maxOffset} mm (vannbrett: ${waterboardText}, dørhåndtak: ${handleDist} mm), som ligger mellom 1 og 50 mm.`;
      } else if (screen.mountingBracket === 'flexible') {
        bracketName = 'Justerbare braketter';
        bracketRule = `Maksimalt utstikk er ${maxOffset} mm (vannbrett: ${waterboardText}, dørhåndtak: ${handleDist} mm), som er over 50 mm.`;
      }
      
      const step5Desc = `${bracketRule}<br>Valgt: <strong>${bracketName}</strong>.`;
      steps.push({ title: step5Title, desc: step5Desc });
    }

    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'sl-modal-overlay';
    
    modalOverlay.innerHTML = `
      <div class="sl-modal-container" style="max-width: 550px;">
        <button type="button" class="sl-modal-close" aria-label="${closeText}">
          <span class="sl-close-icon">&times;</span>
        </button>
        <div class="sl-modal-content">
          <h3 class="sl-modal-heading" style="display: flex; align-items: center; gap: 8px; font-size: 1.8rem; margin: 0 0 1.5rem 0; font-weight: 700;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
            </svg>
            <span>${modalTitle} - ${screen.name || `${isDE ? 'Screen' : (isNO ? 'Screen' : 'Screen')} ${index + 1}`}</span>
          </h3>
          <div style="margin-bottom: 20px; padding: 12px; background: rgba(0,0,0,0.02); border-radius: 8px; border: 1px solid rgba(0,0,0,0.04); font-size: 1.3rem;">
            <div style="display: flex; gap: 16px; margin-bottom: 6px;">
              <span style="font-weight: 600; min-width: 130px; color: var(--sl-text-secondary);">${isDE ? 'Montageart' : (isNO ? 'Monteringstype' : 'Mount type')}:</span>
              <span>${mountTypeText}</span>
            </div>
            <div style="display: flex; gap: 16px;">
              <span style="font-weight: 600; min-width: 130px; color: var(--sl-text-secondary);">${isDE ? 'Fenstertyp' : (isNO ? 'Vindustype' : 'Window type')}:</span>
              <span>${typeText}</span>
            </div>
          </div>
          <div style="display: flex; flex-direction: column; gap: 20px; max-height: 50vh; overflow-y: auto; padding-right: 8px;">
            ${steps.map(step => `
              <div style="border-left: 3px solid #4F46E5; padding-left: 12px; margin-bottom: 4px;">
                <h4 style="margin: 0 0 6px 0; font-size: 1.4rem; font-weight: 600; color: #111827;">${step.title}</h4>
                <p style="margin: 0; color: var(--sl-text-secondary); font-size: 1.3rem; line-height: 1.5;">${step.desc}</p>
              </div>
            `).join('')}
          </div>
          <button class="sl-btn-premium close-footer-btn" style="margin-top: 2rem;">
            ${closeText}
          </button>
        </div>
      </div>
    `;

    const closeHandler = () => {
      this.state.showCalculationModalIndex = null;
      document.body.style.overflow = '';
      this.render();
    };

    modalOverlay.querySelector('.sl-modal-close').onclick = closeHandler;
    modalOverlay.querySelector('.close-footer-btn').onclick = closeHandler;
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        closeHandler();
      }
    };

    return modalOverlay;
  }

  // --- Helpers ---

  renderSelectionGrid(label, fieldName, options, selectedValue, index, type = 'text', layout = 'grid') {
    let gridClass = 'selection-grid';
    if (layout === 'vertical') gridClass = 'selection-grid--vertical';
    if (layout === 'horizontal') gridClass = 'selection-grid--horizontal';

    let html = `
      <div class="field margin-top-md field-name-${fieldName}" data-field-name="${fieldName}">
        <label class="field-label">${label}</label>
        <div class="${gridClass}">
    `;

    options.forEach((opt) => {
      const isSelected = selectedValue === opt.id;
      const isDisabled = opt.disabled === true;
      const cardClass = layout === 'vertical' ? 'selection-card selection-card--vertical' : 'selection-card';

      let visual = '';
      if (opt.hex) {
        visual = `<div class="card-visual"><span class="color-dot" style="background:${opt.hex};"></span></div>`;
      } else if (opt.image) {
        const currentScreen = this.state && this.state.screens && this.state.screens[index];
        const isWiredLeft = currentScreen && currentScreen.motor === 'wired' && (currentScreen.motorPlacement === 'left' || !currentScreen.motorPlacement);
        let imgStyle = '';
        if (fieldName === 'cableExit' && isWiredLeft) {
          imgStyle = 'style="transform: scaleX(-1);"';
        }
        if (layout === 'vertical') {
          visual = `<div class="card-visual"><img src="${opt.image}" alt="${opt.title}" class="fabric-swatch" ${imgStyle} /></div>`;
        } else {
          visual = `<div class="card-visual" ${imgStyle}><div class="fabric-swatch small" style="background-image: url('${opt.image}');"></div></div>`;
        }
      }

      html += `
        <label class="${cardClass} ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}" ${isDisabled ? 'style="opacity: 0.4; pointer-events: none;"' : ''}>
          <input type="radio" name="${fieldName}_${index}" value="${opt.id}" data-field="${fieldName}" ${
            isSelected ? 'checked' : ''
          } ${isDisabled ? 'disabled' : ''} class="hidden-input">
          ${visual}
          <div class="card-text-wrapper">
            <span class="card-title">${opt.title}</span>
            ${opt.desc ? `<span class="card-price">${opt.desc}</span>` : ''}
            ${opt.extraPrice && opt.extraPrice > 0 ? `
              <div style="margin-top: 4px; display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">
                ${opt.extraCompareAtPrice && opt.extraCompareAtPrice > opt.extraPrice ? `<span class="card-price old-price" style="text-decoration: line-through; color: var(--sl-text-subdued); font-size: 12px; font-weight: 400;">+${(opt.extraCompareAtPrice / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}</span>` : ''}
                <span class="card-price" style="font-weight: 600; font-size: 14px; color: #171717;">+${(opt.extraPrice / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}</span>
              </div>
            ` : ''}
          </div>
        </label>
      `;
    });

    html += `</div></div>`;
    return html;
  }

  renderScreenItem(screen, index) {
    const wrapper = document.createElement('details-accordion');
    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const isDE = localeCode === 'de';
    const isNO = localeCode === 'no' || localeCode === 'nb';

    const rawCost = window.ScreenluxEngine.calculateScreenPrice(screen, this.data.config);

    const variant = window.ScreenluxEngine.matchVariant(rawCost, this.data.screens);
    const price = variant ? variant.price : rawCost;
    const compareAtPrice = variant ? variant.compare_at_price : price;

    console.log('SCREEN DEBUG', {
      width: screen.width,
      height: screen.height,
      cassetteSize: screen.cassetteSize,
      motor: screen.motor,
      frameColor: screen.frameColor,
      fabricColor: screen.fabricColor,
      fabricType: screen.fabricType,
      rawCost,
      matchedVariantId: variant?.id,
      matchedVariantTitle: variant?.title,
      matchedVariantPrice: variant?.price,
      finalDisplayedPrice: price
    });

    const frameOptions = this.data.frameColors || [];
    const fabricColors = this.getFabricColorsForType(screen.fabricType);

    // Calculate extra prices for options based on actual variant sale prices (with discounts)
    const getBasePrices = (field, optionsArray) => {
      if (!optionsArray || optionsArray.length === 0) return { price: 0, compareAtPrice: 0 };
      let minPrice = Infinity;
      let minCompareAtPrice = Infinity;
      optionsArray.forEach(opt => {
        const raw = window.ScreenluxEngine.calculateScreenPrice({ ...screen, [field]: opt.id }, this.data.config);
        const variant = window.ScreenluxEngine.matchVariant(raw, this.data.screens);
        const actualPrice = variant ? variant.price : raw;
        // Default to price if compare_at_price is not set or 0
        const compareAtPrice = (variant && variant.compare_at_price > 0) ? variant.compare_at_price : actualPrice;
        if (actualPrice < minPrice) minPrice = actualPrice;
        if (compareAtPrice < minCompareAtPrice) minCompareAtPrice = compareAtPrice;
      });
      return { price: minPrice, compareAtPrice: minCompareAtPrice };
    };

    const mapExtraPrice = (field, optionsArray) => {
      const bases = getBasePrices(field, optionsArray);
      return (optionsArray || []).map(opt => {
        const raw = window.ScreenluxEngine.calculateScreenPrice({ ...screen, [field]: opt.id }, this.data.config);
        const variant = window.ScreenluxEngine.matchVariant(raw, this.data.screens);
        const actualPrice = variant ? variant.price : raw;
        const compareAtPrice = (variant && variant.compare_at_price > 0) ? variant.compare_at_price : actualPrice;
        return { 
          ...opt, 
          extraPrice: Math.max(0, actualPrice - bases.price),
          extraCompareAtPrice: Math.max(0, compareAtPrice - bases.compareAtPrice)
        };
      });
    };

    const fabricTypes = mapExtraPrice('fabricType', this.data.fabrics);
    const cassetteSizes = mapExtraPrice('cassetteSize', this.data.cassetteSizes);
    const motorOptions = mapExtraPrice('motor', this.data.motorOptions);
    const cableExitOptions = this.data.cableExitOptions || [];

    // --- Summary Data Preparation ---
    // Helper to get title or null if not selected
    const getTitle = (currentVal, options) => {
      if (!currentVal) return null;
      const found = options.find((o) => o.id === currentVal);
      return found ? found.title : currentVal;
    };

    const frameLabel = getTitle(screen.frameColor, frameOptions);
    const fabricColorLabel = getTitle(screen.fabricColor, fabricColors);
    const fabricTypeLabel = getTitle(screen.fabricType, fabricTypes);

    let cassetteLabel = getTitle(screen.cassetteSize, cassetteSizes);
    // Clean up "Slim (85x85mm)" -> "Slim"
    if (cassetteLabel && cassetteLabel.includes('(')) {
      cassetteLabel = cassetteLabel.split('(')[0].trim();
    }

    const motorLabel = getTitle(screen.motor, motorOptions);

    // Build the list items dynamically
    const summaryItems = [];

    // 1. Dimensions (Always show if they exist, which they should)
    if (screen.width || screen.height) {
      if (screen.valid) {
        summaryItems.push({ text: `${screen.width || 0} × ${screen.height || 0} mm`, class: '' });
      } else {
        const text = isDE 
          ? `${screen.width || 0} × ${screen.height || 0} mm (Ungültige Maße)`
          : (isNO ? `${screen.width || 0} × ${screen.height || 0} mm (Ugyldige mål)` : `${screen.width || 0} × ${screen.height || 0} mm (Invalid dimensions)`);
        summaryItems.push({ text: text, style: 'color: #DC2626 !important; font-weight: 600;' });
      }
    } else {
      summaryItems.push({ text: window.ScreenluxTranslations.screenSummary.dimensionsNotSet, class: 'placeholder' });
    }
    
    // Hidden: Cassette, Frame, Fabric, Motor as per user request to only show measurements.

    const guaranteeActiveText = window.ScreenluxTranslations.measurementGuide && window.ScreenluxTranslations.measurementGuide.activeBadge;
    const guaranteeBadgeHtml = screen.measurementGuarantee && guaranteeActiveText 
      ? `<span class="guarantee-badge-inline" style="background-color: #E6F4EA; color: #137333; font-size: 11px; font-weight: 500; padding: 2px 6px; border-radius: 4px; margin-left: 8px; display: inline-flex; align-items: center; gap: 4px; border: 1px solid #A3E2AB; vertical-align: middle;">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="#137333" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ${guaranteeActiveText}
         </span>`
      : '';


    const isEst = !!screen.rawIsEstimate;
    const urlParams = new URLSearchParams(window.location.search);
    const customerId = urlParams.get('logged_in_customer_id') || urlParams.get('customer_id') || this.dataset.customerId;
    const baseLink = (window.ScreenluxTranslations.measurementGuide && window.ScreenluxTranslations.measurementGuide.link) || '/pages/measure-app';
    let linkPath = isEst ? '/pages/measure-app' : baseLink;
    if (customerId) {
      const separator = linkPath.includes('?') ? '&' : '?';
      linkPath = `${linkPath}${separator}customer_id=${customerId}&logged_in_customer_id=${customerId}`;
    }

    let infoBoxHtml = '';
    if (screen.measurementGuarantee) {
      const title = isDE ? '100% Passgarantie aktiv' : (isNO ? '100% Målegaranti aktiv' : '100% Fit Guarantee active');
      const desc = isDE 
        ? 'Deine Maße sind durch die 100% Passgarantie geschützt.' 
        : (isNO ? 'Dine mål er beskyttet av målegarantien. Hvis screensene ikke passer, erstatter vi dem gratis.' : 'Your measurements are protected by the guarantee.');
      const note = isDE
        ? 'Hinweis: Wenn du die Maße manuell änderst, verlierst du die 100% Passgarantie.'
        : (isNO ? 'Merk: Hvis du endrer målene manuelt, mister du garantien.' : 'Note: If you change the measurements manually, you will lose the guarantee.');
      const readMoreText = isDE ? 'Mehr erfahren' : (isNO ? 'Les mer' : 'Read more');
      
      infoBoxHtml = `
        <div class="guarantee-info-box active" style="margin-top: 16px; padding: 16px; background-color: #E6F4EA; border: 1px solid #A3E2AB; border-radius: 8px; text-align: left;">
          <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #137333; font-size: 14px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#137333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 11 2 2 4-4"/>
            </svg>
            <span>${title}</span>
          </div>
          <div style="font-size: 14px; color: #137333; line-height: 1.5; margin-top: 8px;">
            ${desc}
          </div>
          <div style="font-size: 13px; color: #137333; line-height: 1.4; margin-top: 8px; font-style: italic; opacity: 0.95;">
            ${note}
          </div>
          <div style="margin-top: 12px; display: flex; gap: 16px; align-items: center; flex-wrap: wrap;">
            <button class="guarantee-read-more-btn" type="button" style="background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #137333; text-decoration: underline; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
              ${readMoreText}
            </button>
            ${(screen.rawWidthTop || screen.rawHeightLeft) ? `
              <button class="show-calculation-btn" type="button" style="background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #137333; text-decoration: underline; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
                ${isDE ? 'Berechnung anzeigen' : (isNO ? 'Vis utregning' : 'Show calculation')}
              </button>
            ` : ''}
          </div>
        </div>
      `;
    } else if (screen.originalWidth && screen.originalHeight) {
      const title = isDE ? '100% Passgarantie inaktiv' : (isNO ? '100% Målegaranti inaktiv' : 'Guarantee lost');
      const desc = isDE
        ? 'Die Maße wurden manuell geändert. Setze die Maße auf die gemessenen Werte zurück, um die 100% Passgarantie wieder zu aktivieren.'
        : (isNO ? 'Målene har blitt endret manuelt. Gjenopprett de opprinnelige målene for å reaktivere målegarantien.' : 'The measurements have been manually modified. Restore the original measurements to reactivate the guarantee.');
      const cta = isDE
        ? `Originalmaße wiederherstellen (${screen.originalWidth} × ${screen.originalHeight} mm) →`
        : (isNO ? `Gjenopprett opprinnelige mål (${screen.originalWidth} × ${screen.originalHeight} mm) →` : `Restore original measurements (${screen.originalWidth} × ${screen.originalHeight} mm) →`);

      infoBoxHtml = `
        <div class="guarantee-info-box lost" style="margin-top: 16px; padding: 16px; background-color: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px; text-align: left;">
          <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: #D97706; font-size: 14px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>${title}</span>
          </div>
          <div style="font-size: 14px; color: #92400E; line-height: 1.5; margin-top: 8px;">
            ${desc}
          </div>
          <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
            <button type="button" class="btn-restore-guarantee" style="background: none; border: none; padding: 0; font-size: 14px; font-weight: 600; color: #B45309; text-decoration: underline; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; text-align: left;">
              ${cta}
            </button>
            ${(screen.rawWidthTop || screen.rawHeightLeft) ? `
              <button class="show-calculation-btn" type="button" style="background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #B45309; text-decoration: underline; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; text-align: left;">
                ${isDE ? 'Berechnung anzeigen' : (isNO ? 'Vis utregning' : 'Show calculation')}
              </button>
            ` : ''}
          </div>
        </div>
      `;
    } else {
      const title = isEst 
        ? (isDE ? 'Schätzpreis (Keine 100% Passgarantie)' : (isNO ? 'Prisoverslag (Ingen 100% målegaranti)' : 'Price Estimate (No Guarantee)'))
        : (isDE ? '100% Passgarantie' : (isNO ? '100% Målegaranti' : '100% Fit Guarantee'));
      const desc = isEst
        ? (isDE 
            ? 'Dieses Fenster wurde per KI geschätzt. Um die 100% Passgarantie zu aktivieren, verwende den Aufmaß-Assistenten und miss physisch nach.'
            : (isNO 
                ? 'Dette vinduet ble estimert med AI. For å få 100% målegaranti må du bruke måleappen og utføre fysisk kontrollmåling.' 
                : 'This window was estimated using AI. To get the 100% fit guarantee, please use the measuring app and perform physical measurements.'))
        : (isDE
            ? '<strong>Sollten die Screens nicht passen, ersetzen wir sie kostenlos.</strong> Verwende den Aufmaß-Assistenten, damit wir die richtige Größe für dich berechnen und die 100% Passgarantie aktivieren.'
            : (isNO ? '<strong>Hvis screensene ikke passer, erstatter vi dem gratis.</strong> Bruk måleguiden så beregner vi riktig størrelse for deg og aktivere målegarantien.' : '<strong>If the screens don\'t fit, we replace them for free.</strong> Use the measuring assistant and we will calculate the right size for you and activate the guarantee.'));
      const ctaText = isEst
        ? (isDE ? 'Jetzt kontrollmessen' : (isNO ? 'Utfør kontrollmåling nå' : 'Perform check measurement'))
        : (isDE ? 'Zum Aufmaß-Assistenten' : (isNO ? 'Gå til måleapp' : 'Go to Mess-App'));

      const arrowSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-left: 6px; flex-shrink: 0;"><path d="M20.7806 12.5306L14.0306 19.2806C13.8899 19.4213 13.699 19.5003 13.5 19.5003C13.301 19.5003 13.1101 19.4213 12.9694 19.2806C12.8286 19.1398 12.7496 18.949 12.7496 18.7499C12.7496 18.5509 12.8286 18.36 12.9694 18.2193L18.4397 12.7499H3.75C3.55109 12.7499 3.36032 12.6709 3.21967 12.5303C3.07902 12.3896 3 12.1988 3 11.9999C3 11.801 3.07902 11.6103 3.21967 11.4696C3.36032 11.3289 3.55109 11.2499 3.75 11.2499H18.4397L12.9694 5.78055C12.8286 5.63982 12.7496 5.44895 12.7496 5.24993C12.7496 5.05091 12.8286 4.86003 12.9694 4.7193C13.1101 4.57857 13.301 4.49951 13.5 4.49951C13.699 4.49951 13.8899 4.57857 14.0306 4.7193L20.7806 11.4693C20.8504 11.539 20.9057 11.6217 20.9434 11.7127C20.9812 11.8038 21.0006 11.9014 21.0006 11.9999C21.0006 12.0985 20.9812 12.1961 20.9434 12.2871C20.9057 12.3782 20.8504 12.4609 20.7806 12.5306Z" fill="currentColor"/></svg>`;

      const gTitleColor = isEst ? '#D97706' : '#3730A3';
      const gDescColor = isEst ? '#92400E' : '#312E81';
      const gCtaStyle = isEst
        ? ''
        : 'width: 100%; background-color: var(--sl-color-primary, #5b5bf9); border-color: var(--sl-color-primary, #5b5bf9); color: #ffffff;';

      infoBoxHtml = `
        <div class="guarantee-info-box default" style="margin-top: 16px; padding: 16px; background-color: ${isEst ? '#FFFBEB' : '#F5F6FF'}; border: 1px solid ${isEst ? '#FDE68A' : '#E0E7FF'}; border-radius: 8px; text-align: left;">
          <div style="display: flex; align-items: center; gap: 8px; font-weight: 600; color: ${gTitleColor}; font-size: 16px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${gTitleColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>${title}</span>
          </div>
          <div style="font-size: 14px; color: ${gDescColor}; line-height: 1.5; margin-top: 8px;">
            ${desc}
          </div>
          <div style="margin-top: 12px;">
            <a href="${linkPath}" class="info-card-btn" target="_blank" style="${gCtaStyle}">
              <span>${ctaText}</span>${arrowSvg}
            </a>
          </div>
        </div>
      `;
    }

    wrapper.innerHTML = `
      <details ${screen.expanded ? 'open' : ''} class="${screen.valid ? '' : 'invalid'}">
        <summary>
          <div class="screen-summary-container">
              <div class="screen-info">
                <span class="screen-title" style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                  ${screen.name || `${window.ScreenluxTranslations.screenSummary.prefix} ${index + 1}`}
                  ${guaranteeBadgeHtml}
                </span>
                <div class="screen-summary-list" style="margin-top: 4px;">
                   ${summaryItems.map((item) => `<div class="${item.class}" style="color: var(--sl-text-secondary); font-size: 14px;">${item.text}</div>`).join('')}
                </div>
             </div>
             <div class="screen-price-container" style="display:flex; align-items:center;">
                 <div class="price-group" style="display:flex; flex-direction:column; align-items:flex-end; margin-right:8px;">
                     ${(screen.valid && compareAtPrice > price) ? `<span class="price-old" style="font-size:12px; text-decoration:line-through; color:var(--sl-text-subdued); line-height:1; margin-bottom:2px;">${(compareAtPrice / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}</span>` : ''}
                     <span class="screen-price" style="line-height:1; color: ${screen.valid ? 'inherit' : '#DC2626'};">${screen.valid ? `${(price / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}` : '—'}</span>
                 </div>
                 <svg class="accordion-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="#171717" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                 </svg>
             </div>
          </div>
        </summary>
        <div class="accordion-body accordion__content">
           <!-- Dimensions -->
           <div class="grid-2"> 
             <div class="field">
               <label class="field-label">${window.ScreenluxTranslations.dimensions.width}</label>
               <input type="number" inputmode="numeric" pattern="[0-9]*" 
                      value="${screen.width}" 
                      data-field="width" 
                      class="sl-input ${screen.errors.dimension && !screen.width ? 'error' : ''}">
             </div>
             <div class="field">
               <label class="field-label">${window.ScreenluxTranslations.dimensions.height}</label>
               <input type="number" inputmode="numeric" pattern="[0-9]*" 
                      value="${screen.height}" 
                      data-field="height"
                      class="sl-input ${screen.errors.dimension && !screen.height ? 'error' : ''}">
             </div>
           </div>
           
           ${infoBoxHtml}
           
           ${
             !screen.valid
               ? `<div class="text-xs margin-top-xs" style="color:var(--sl-color-error)">⚠️ ${
                   screen.errors.dimension || window.ScreenluxTranslations.dimensions.invalid
                 }</div>`
               : ''
           }
           
           <!-- Cassette Size Selector -->
           ${(() => {
               // The slim cassette is not offered past the limits in the engine.
               const isTooLargeForSlim = window.ScreenluxEngine.cassetteFor(screen.width, screen.height) === 'large';

               const cassetteSizesWithAvailability = cassetteSizes.map(opt => ({
                 ...opt,
                 disabled: opt.id === 'slim' && isTooLargeForSlim
               }));

               // Auto-switch to large if slim is selected but disabled
               if (screen.cassetteSize === 'slim' && isTooLargeForSlim) {
                screen.cassetteSize = 'large';
              }

              return this.renderSelectionGrid(
                window.ScreenluxTranslations.options.cassetteSize,
                'cassetteSize',
                cassetteSizesWithAvailability,
                screen.cassetteSize,
                index,
                'image',
                'vertical',
              );
            })()}

           <!-- Fabric Type Selector (function first, drives available colours) -->
           ${this.renderSelectionGrid(
             window.ScreenluxTranslations.options.fabricTransparency,
             'fabricType',
             fabricTypes,
             screen.fabricType,
             index,
             'image',
             'vertical',
           )}

           <!-- Frame Color Selector -->
           ${this.renderSelectionGrid(
             window.ScreenluxTranslations.options.frameColor,
             'frameColor',
             frameOptions,
             screen.frameColor,
             index,
             'color',
             'horizontal',
           )}

           <!-- Fabric Color Selector -->
           ${this.renderSelectionGrid(
             window.ScreenluxTranslations.options.fabricColor,
             'fabricColor',
             fabricColors,
             screen.fabricColor,
             index,
             'color',
             'horizontal',
           )}

           <!-- Motor Selector -->
           ${this.renderSelectionGrid(window.ScreenluxTranslations.options.motor, 'motor', motorOptions, screen.motor, index, 'image', 'vertical')}

           <!-- Motor Placement Selector -->
           ${screen.motor === 'wired' && this.data.motorPlacementOptions && this.data.motorPlacementOptions.length > 0 ? this.renderSelectionGrid(window.ScreenluxTranslations.options.motor_placement, 'motorPlacement', this.data.motorPlacementOptions, screen.motorPlacement || 'left', index, 'image', 'vertical') : ''}

           <!-- Cable Exit Selector -->
           ${screen.motor === 'wired' && cableExitOptions.length > 0 ? this.renderSelectionGrid(window.ScreenluxTranslations.options.cableExitDirection, 'cableExit', cableExitOptions, screen.cableExit, index, 'image', 'vertical') : ''}
           
           <!-- Mounting Bracket Selector (NO Market Only) -->
           ${(window.ScreenluxData.config && window.ScreenluxData.config.currencyCode === 'NOK') ? (() => {
             const frameColor = screen.frameColor || 'anthracite';
             const bracketOptions = (this.data.bracketOptions || []).map(opt => {
               let img = null;
               if (opt.id !== 'none' && this.data.assets && this.data.assets.bracketImages && this.data.assets.bracketImages[opt.id]) {
                 img = this.data.assets.bracketImages[opt.id][frameColor];
               }
               return { ...opt, image: img };
             });
             
             const bases = getBasePrices('mountingBracket', bracketOptions);
             const bracketOptionsWithPrice = bracketOptions.map(opt => {
               const raw = window.ScreenluxEngine.calculateScreenPrice({ ...screen, mountingBracket: opt.id }, this.data.config);
               const variant = window.ScreenluxEngine.matchVariant(raw, this.data.screens);
               const actualPrice = variant ? variant.price : raw;
               const compareAtPrice = (variant && variant.compare_at_price > 0) ? variant.compare_at_price : actualPrice;
               return { 
                 ...opt,
                 desc: opt.id !== 'none' ? `${window.ScreenluxTranslations.options.setOf || 'Sett med'} ${screen.height > 2700 ? 8 : 6}x ${opt.title.toLowerCase()}` : undefined,
                 extraPrice: Math.max(0, actualPrice - bases.price),
                 extraCompareAtPrice: Math.max(0, compareAtPrice - bases.compareAtPrice)
               };
             });

             return this.renderSelectionGrid(
               window.ScreenluxTranslations.options.mountingBrackets || 'Monteringsbraketter',
               'mountingBracket',
               bracketOptionsWithPrice,
               screen.mountingBracket,
               index,
               'image',
               'vertical'
             );
           })() : ''}
           
           <!-- Apply this screen's options to every other screen -->
           ${
             this.state.screens.length > 1
               ? (() => {
                   const applied = this.state.appliedOptionsToAllId === screen.id;
                   return `
           <div class="apply-to-all">
              <button type="button" class="info-card-btn apply-to-all-btn${applied ? ' is-applied' : ''}">
                ${
                  applied
                    ? `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px; flex-shrink: 0;"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                       ${window.ScreenluxTranslations.actions.applyToAllDone}`
                    : `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px; flex-shrink: 0;"><path d="M13.5 1.75H5.5C5.30109 1.75 5.11032 1.82902 4.96967 1.96967C4.82902 2.11032 4.75 2.30109 4.75 2.5V4.75H2.5C2.30109 4.75 2.11032 4.82902 1.96967 4.96967C1.82902 5.11032 1.75 5.30109 1.75 5.5V13.5C1.75 13.6989 1.82902 13.8897 1.96967 14.0303C2.11032 14.171 2.30109 14.25 2.5 14.25H10.5C10.6989 14.25 10.8897 14.171 11.0303 14.0303C11.171 13.8897 11.25 13.6989 11.25 13.5V11.25H13.5C13.6989 11.25 13.8897 11.171 14.0303 11.0303C14.171 10.8897 14.25 10.6989 14.25 10.5V2.5C14.25 2.30109 14.171 2.11032 14.0303 1.96967C13.8897 1.82902 13.6989 1.75 13.5 1.75ZM9.75 12.75H3.25V6.25H9.75V12.75ZM12.75 9.75H11.25V5.5C11.25 5.30109 11.171 5.11032 11.0303 4.96967C10.8897 4.82902 10.6989 4.75 10.5 4.75H6.25V3.25H12.75V9.75Z" fill="currentColor"/></svg>
                       ${window.ScreenluxTranslations.actions.applyToAll}`
                }
              </button>
              <div class="apply-to-all-hint">${window.ScreenluxTranslations.actions.applyToAllHint}</div>
           </div>
                   `;
                 })()
               : ''
           }

           <!-- Actions -->
           <div class="margin-top-md" style="display:flex; justify-content: space-between; align-items: center;">
              <button type="button" class="btn btn-text remove-screen-btn" style="color:var(--sl-color-error); padding:0; width:auto; display: inline-flex; align-items: center;">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px;">
                  <g clip-path="url(#clip0_128_400)">
                    <path d="M13.5 3H11.25V2.25C11.25 1.78587 11.0656 1.34075 10.7374 1.01256C10.4092 0.684374 9.96413 0.5 9.5 0.5H6.5C6.03587 0.5 5.59075 0.684374 5.26256 1.01256C4.93437 1.34075 4.75 1.78587 4.75 2.25V3H2.5C2.30109 3 2.11032 3.07902 1.96967 3.21967C1.82902 3.36032 1.75 3.55109 1.75 3.75C1.75 3.94891 1.82902 4.13968 1.96967 4.28033C2.11032 4.42098 2.30109 4.5 2.5 4.5H2.75V13C2.75 13.3315 2.8817 13.6495 3.11612 13.8839C3.35054 14.1183 3.66848 14.25 4 14.25H12C12.3315 14.25 12.6495 14.1183 12.8839 13.8839C13.1183 13.6495 13.25 13.3315 13.25 13V4.5H13.5C13.6989 4.5 13.8897 4.42098 14.0303 4.28033C14.171 4.13968 14.25 3.94891 14.25 3.75C14.25 3.55109 14.171 3.36032 14.0303 3.21967C13.8897 3.07902 13.6989 3 13.5 3ZM6.25 2.25C6.25 2.1837 6.27634 2.12011 6.32322 2.07322C6.37011 2.02634 6.4337 2 6.5 2H9.5C9.5663 2 9.62989 2.02634 9.67678 2.07322C9.72366 2.12011 9.75 2.1837 9.75 2.25V3H6.25V2.25ZM11.75 12.75H4.25V4.5H11.75V12.75ZM7.25 6.5V10.5C7.25 10.6989 7.17098 10.8897 7.03033 11.0303C6.88968 11.171 6.69891 11.25 6.5 11.25C6.30109 11.25 6.11032 11.171 5.96967 11.0303C5.82902 10.8897 5.75 10.6989 5.75 10.5V6.5C5.75 6.30109 5.82902 6.11032 5.96967 5.96967C6.11032 5.82902 6.30109 5.75 6.5 5.75C6.69891 5.75 6.88968 5.82902 7.03033 5.96967C7.17098 6.11032 7.25 6.30109 7.25 6.5ZM10.25 6.5V10.5C10.25 10.6989 10.171 10.8897 10.0303 11.0303C9.88968 11.171 9.69891 11.25 9.5 11.25C9.30109 11.25 9.11032 11.171 8.96967 11.0303C8.82902 10.8897 8.75 10.6989 8.75 10.5V6.5C8.75 6.30109 8.82902 6.11032 8.96967 5.96967C9.11032 5.82902 9.30109 5.75 9.5 5.75C9.69891 5.75 9.88968 5.82902 10.0303 5.96967C10.171 6.11032 10.25 6.30109 10.25 6.5Z" fill="#EF4444"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_128_400">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                ${window.ScreenluxTranslations.actions.removeScreen}
              </button>
              <button type="button" class="btn btn-text duplicate-screen-btn" style="padding:0; width:auto; color: var(--sl-text-secondary); display: inline-flex; align-items: center;">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 6px;">
                  <path d="M13.5 1.75H5.5C5.30109 1.75 5.11032 1.82902 4.96967 1.96967C4.82902 2.11032 4.75 2.30109 4.75 2.5V4.75H2.5C2.30109 4.75 2.11032 4.82902 1.96967 4.96967C1.82902 5.11032 1.75 5.30109 1.75 5.5V13.5C1.75 13.6989 1.82902 13.8897 1.96967 14.0303C2.11032 14.171 2.30109 14.25 2.5 14.25H10.5C10.6989 14.25 10.8897 14.171 11.0303 14.0303C11.171 13.8897 11.25 13.6989 11.25 13.5V11.25H13.5C13.6989 11.25 13.8897 11.171 14.0303 11.0303C14.171 10.8897 14.25 10.6989 14.25 10.5V2.5C14.25 2.30109 14.171 2.11032 14.0303 1.96967C13.8897 1.82902 13.6989 1.75 13.5 1.75ZM9.75 12.75H3.25V6.25H9.75V12.75ZM12.75 9.75H11.25V5.5C11.25 5.30109 11.171 5.11032 11.0303 4.96967C10.8897 4.82902 10.6989 4.75 10.5 4.75H6.25V3.25H12.75V9.75Z" fill="#171717"/>
                </svg>
                ${window.ScreenluxTranslations.actions.duplicateScreen}
              </button>
           </div>
        </div>
      </details>
    `;

    // Remove Listener
    const removeBtn = wrapper.querySelector('.remove-screen-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeScreen(index);
      });
    }

    // Duplicate Listener
    const duplicateBtn = wrapper.querySelector('.duplicate-screen-btn');
    if (duplicateBtn) {
      duplicateBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleDuplicateScreen(index);
      });
    }

    // Apply Options To All Listener
    const applyAllBtn = wrapper.querySelector('.apply-to-all-btn');
    if (applyAllBtn) {
      applyAllBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleApplyOptionsToAll(index);
      });
    }

    // Restore Guarantee Listener
    const restoreBtn = wrapper.querySelector('.btn-restore-guarantee');
    if (restoreBtn) {
      restoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.restoreOriginalMeasurements(index);
      });
    }

    // Read More Listener
    const readMoreBtn = wrapper.querySelector('.guarantee-read-more-btn');
    if (readMoreBtn) {
      readMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.state.showGuaranteeDetailsModal = true;
        this.render();
      });
    }

    // Show Calculation Listener
    const showCalcBtn = wrapper.querySelector('.show-calculation-btn');
    if (showCalcBtn) {
      showCalcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.state.showCalculationModalIndex = index;
        this.render();
      });
    }

    // Event Delegation for inputs
    wrapper.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', (e) => {
        const field = e.target.dataset.field;
        if (field) {
          const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
          this.updateScreen(index, field, val);
        }
      });

      // Jump to SVG configurator when measurement inputs are focused
      if (input.dataset.field === 'width' || input.dataset.field === 'height') {
        input.addEventListener('focus', () => {
          this.jumpToSVGConfigurator();
        });
      }
    });

    // Handle Summary Click manually for Accordion State
    wrapper.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleScreenAccordion(index);
    });

    return wrapper;
  }

  renderInstallationSection() {
    const section = document.createElement('div');
    section.className = 'configurator-group-box';
    section.id = 'installation-section';

    // 1. Group Title
    const title = document.createElement('label');
    title.className = 'grouping-title';
    title.textContent = window.ScreenluxTranslations.installationTitle;
    section.appendChild(title);

    // 2. Global Solar Check (Upsell / Tip)
    // Show tip if any screen is NOT solar (i.e. wired or undefined)
    if (this.state.screens.some((s) => s.motor !== 'solar')) {
      const currentEst = this.getEstimatedInstallationPrice(this.state.screens);
      const allSolarScreens = this.state.screens.map((s) => ({ ...s, motor: 'solar' }));
      const allSolarEst = this.getEstimatedInstallationPrice(allSolarScreens);
      const savings = currentEst > allSolarEst ? currentEst - allSolarEst : 0;

      const solarTip = document.createElement('div');
      solarTip.className = 'info-card-tip';

      const rawTipFooter = window.ScreenluxTranslations.tipFooterHtml || '';
      const tipFooterText = rawTipFooter
        ? rawTipFooter.replace(/12\.000\s?kr|1200\s?€|1\.200\s?€|1,200\s?€|1200/g, '~ ' + (savings / 100).toFixed(0) + ' ' + (window.ScreenluxData?.config?.currencySymbol || '€'))
        : '';
      solarTip.innerHTML = `
        <div class="info-card-header">
           <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="benefit-check" style="color:var(--sl-color-success)">
             <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-2 15l-5-5 1.41-1.41L8 12.17l7.59-7.59L17 6l-9 9z" fill="currentColor"/>
           </svg>
           <span class="info-card-title">${window.ScreenluxTranslations.tipTitle}</span>
        </div>
        <div class="info-card-body">
           <div class="info-card-message">
             ${window.ScreenluxTranslations.tipMessageHtml}
           </div>
           <ul class="info-card-benefits">
             <li class="info-card-benefit">
               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
               ${window.ScreenluxTranslations.tipBenefits.noWiring}
             </li>
             <li class="info-card-benefit">
               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
               ${window.ScreenluxTranslations.tipBenefits.noWallDrilling}
             </li>
             <li class="info-card-benefit">
               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
               ${window.ScreenluxTranslations.tipBenefits.noElectrician}
             </li>
           </ul>
           <div class="info-card-footer">
             ${tipFooterText ? `<span class="info-card-footer-text">${tipFooterText}</span>` : ''}
             <button type="button" class="info-card-btn">${window.ScreenluxTranslations.switchToSolar}</button>
           </div>
        </div>
      `;
      const btn = solarTip.querySelector('.info-card-btn');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleGlobalSolar();
        });
      }
      section.appendChild(solarTip);
    }

    const estInstallPrice = this.getEstimatedInstallationPrice(this.state.screens);
    const allSolarScreensForOptions = this.state.screens.map((s) => ({ ...s, motor: 'solar' }));
    const allSolarEstForOptions = this.getEstimatedInstallationPrice(allSolarScreensForOptions);
    const savingsOptions = estInstallPrice > allSolarEstForOptions ? estInstallPrice - allSolarEstForOptions : 0;

    const profDesc = window.ScreenluxTranslations.installationProfessional.desc;
    const profPriceFormat = `~ ${(estInstallPrice / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}`;
    const profNote = savingsOptions > 0 
      ? window.ScreenluxTranslations.installationProfessional.note.replace(/12[\s\.]?000\s?kr|1200\s?€|1[\.,]?200\s?€|1,200\s?€|1200/g, '~ ' + (savingsOptions / 100).toFixed(0) + ' ' + (window.ScreenluxData?.config?.currencySymbol || '€'))
      : '';

    // Installation type options
    const installationOptions = [
      {
        id: 'diy',
        title: window.ScreenluxTranslations.installationDiy.title,
        desc: window.ScreenluxTranslations.installationDiy.desc,
        price: window.ScreenluxTranslations.installationDiy.price,
      },
      {
        id: 'professional',
        title: window.ScreenluxTranslations.installationProfessional.title,
        desc: profDesc,
        price: profPriceFormat,
        note: profNote,
      },
    ];

    // Render installation type selection using vertical cards
    // Note: Removed "grouping-title" from inside html because it is now at the top of the section
    const html = `
      <div class="field margin-top-md">
        <div class="selection-grid--vertical">
          ${installationOptions
            .map((opt) => {
              const isSelected = this.state.installationType === opt.id;

              // Standardized price rendering
              let priceDisplay = '';
              const diyBadgeLabel = window.ScreenluxTranslations.installationDiy.price || '';
              if (!opt.price) {
                // No price string for this option in the active locale: render no badge at all.
                priceDisplay = '';
              } else if (diyBadgeLabel && opt.price === diyBadgeLabel) {
                priceDisplay = `<span style="background-color: #A7F3D0; color: #064E3B; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">${diyBadgeLabel}</span>`;
              } else {
                // Ensure standardized look for other prices
                priceDisplay = `<span style="font-weight: 600; font-size: 14px;">${opt.price}</span>`;
              }

              return `
              <label class="selection-card selection-card--vertical ${isSelected ? 'selected' : ''}">
                <input type="radio" name="installationType" value="${opt.id}" ${
                  isSelected ? 'checked' : ''
                } class="hidden-input">
                <div class="card-text-wrapper" style="flex: 1;">
                  <span class="card-title">${opt.title}</span>
                  <span class="card-desc" style="white-space: pre-line; margin-top: 4px; display: block;">${
                    opt.desc
                  }</span>
                  ${priceDisplay ? `<div style="margin-top: 8px;">${priceDisplay}</div>` : ''}
                  ${
                    opt.note
                      ? `<span class="card-note" style="color: #F97316; font-size: 13px; margin-top: 4px; display: block;">${opt.note}</span>`
                      : ''
                  }
                </div>
              </label>
            `;
            })
            .join('')}
        </div>
      </div>
    `;

    const optionsContainer = document.createElement('div');
    optionsContainer.innerHTML = html;
    section.appendChild(optionsContainer);

    // Add event listeners for installation type
    section.querySelectorAll('input[name="installationType"]').forEach((radio) => {
      radio.addEventListener('change', (e) => {
        this.setInstallationType(e.target.value);
      });
    });

    // DIY Brackets Section removed/hidden as requested
    /*
    if (this.state.installationType === 'diy') {
      // brackets code hidden
    }
    */

    return section;
  }

  renderBracketCard(bracket) {
    const isSelected = this.state.selectedBracketId === bracket.id;
    const priceText =
      bracket.price === 0
        ? '<span style="background-color: #A7F3D0; color: #064E3B; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">Free</span>'
        : `${(bracket.price / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'} per screen`;

    // Custom image for Unsure or bracket image
    let imageHtml = '';
    // Special handling for the "Unsure" item if it comes from Shopify data or has id 'unsure'
    // But user requested to use price logic mainly. We will keep ID check just for specific image placeholder if needed,
    // or if the product coming from Shopify is likely to be the "Unsure" one.
    // Ideally we should use the product image from Shopify.
    // If bracket.image exists, use it.

    if (bracket.image) {
      imageHtml = `<img src="${bracket.image}" alt="${bracket.title}" style="width: 80px; height: 80px; object-fit: contain; border-radius: 8px; background: #F9FAFB; padding: 4px;">`;
    } else {
      // Fallback or specific placeholder if we want to detect "unsure" by title or id
      // For now, standard fallback
      imageHtml = `<div style="width: 80px; height: 80px; background: #F3F4F6; border-radius: 8px;"></div>`;
    }

    const label = document.createElement('label');
    label.className = `selection-card selection-card--vertical ${isSelected ? 'selected' : ''}`;
    label.style.cursor = 'pointer';

    label.innerHTML = `
      <input type="radio" name="bracket_selection" value="${bracket.id}" class="hidden-input" ${
        isSelected ? 'checked' : ''
      }>
      <div class="card-visual" style="margin-right: 16px;">
        ${imageHtml}
      </div>
      <div class="card-text-wrapper" style="flex: 1;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
             <span class="card-title" style="font-weight: 600;">${bracket.title}</span>
        </div>
        <span class="card-desc" style="white-space: pre-line; margin-top: 4px; display: block; color: #6B7280; font-size: 14px;">${
          bracket.description || ''
        }</span>
        <div style="margin-top: 8px; font-weight: 600; font-size: 14px;">${priceText}</div>
      </div>
    `;

    label.querySelector('input').addEventListener('change', () => {
      this.state.selectedBracketId = bracket.id;
      this.render();
    });

    return label;
  }

  renderExtraSection(dataKey, title, emptyText, hideIfEmpty = false) {
    const items = this.data[dataKey] || [];
    if (items.length === 0 && hideIfEmpty) {
      return null;
    }

    const section = document.createElement('div');
    section.className = 'configurator-group-box';

    if (items.length === 0) {
      section.innerHTML = `<label class="grouping-title">${title}</label><p class="text-subdued"><em>${emptyText}</em></p>`;
      return section;
    }

    section.innerHTML = `<label class="grouping-title">${title}</label>`;

    items.forEach((item) => {
      const card = this.renderExtraCard(item, dataKey);
      section.appendChild(card);
    });

    return section;
  }

  renderExtraCard(item, dataKey) {
    if (!this.state[dataKey]) {
      this.state[dataKey] = {};
    }

    const quantity = this.state[dataKey][item.id] || 0;

    const card = document.createElement('div');
    card.className = `product-card margin-top-sm ${quantity > 0 ? 'selected' : ''}`;

    const factor = quantity > 0 ? quantity : 1;
    const displayPrice = ((item.price * factor) / 100).toFixed(0);
    const displayCompareAtPrice = ((item.compare_at_price * factor) / 100).toFixed(0);

    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const learnMoreText = localeCode === 'de' ? 'Mehr erfahren' : (localeCode === 'no' || localeCode === 'nb' ? 'Les mer' : 'Learn more');

    card.innerHTML = `
      <div class="product-card__image">
        ${
          item.image
            ? `<img src="${item.image}" alt="${item.title}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;">`
            : '<div style="width: 80px; height: 80px; background: #F3F4F6; border-radius: 8px;"></div>'
        }
      </div>
      <div class="product-card__body">
        <div class="product-card__title">${item.title}</div>
        <div class="product-card__price-wrapper" style="display:flex; flex-direction:column; align-items:flex-start; margin-top:4px;">
           ${item.compare_at_price > item.price ? `<span class="price-old" style="font-size:12px; text-decoration:line-through; color:var(--sl-text-subdued); line-height:1; margin-bottom:2px;">${displayCompareAtPrice} ${window.ScreenluxData?.config?.currencySymbol || '€'}</span>` : ''}
           <div class="product-card__price" style="line-height:1;">${displayPrice} ${window.ScreenluxData?.config?.currencySymbol || '€'}</div>
        </div>
        
        <div class="product-card__actions" style="gap: 16px; margin-top: 4px;">
        ${
          quantity === 0
            ? `<button class="btn-add-extra" data-extra-id="${item.id}" style="margin: 0;">${window.ScreenluxTranslations.add}</button>`
            : `<div class="qty-control-group" style="margin: 0;">
            <button class="qty-btn qty-minus" data-extra-id="${item.id}">−</button>
            <span class="qty-value">${quantity}</span>
            <button class="qty-btn qty-plus" data-extra-id="${item.id}">+</button>
          </div>`
        }
          <a href="#" class="more-info-link" style="font-size: 14px; font-weight: 500; color: var(--sl-text-secondary, #4b5563); text-decoration: underline; text-underline-offset: 4px; text-decoration-thickness: 1px; text-decoration-color: rgba(75, 85, 99, 0.3); cursor: pointer; white-space: nowrap;">${learnMoreText}</a>
        </div>
      </div>
    `;

    const addBtn = card.querySelector('.btn-add-extra');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        if (!this.state[dataKey]) this.state[dataKey] = {};
        this.state[dataKey][item.id] = 1;
        this.render();
      });
    }

    const minusBtn = card.querySelector('.qty-minus');
    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        if (this.state[dataKey][item.id] > 0) {
          this.state[dataKey][item.id]--;
          if (this.state[dataKey][item.id] === 0) {
            delete this.state[dataKey][item.id];
          }
          this.render();
        }
      });
    }

    const plusBtn = card.querySelector('.qty-plus');
    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        this.state[dataKey][item.id]++;
        this.render();
      });
    }

    const moreInfoLink = card.querySelector('.more-info-link');
    if (moreInfoLink) {
      moreInfoLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.showProductDetailsModal(item);
      });
    }

    return card;
  }

  showProductDetailsModal(item) {
    document.body.style.overflow = 'hidden';
    const localeCode = (this.data.locale || 'de').toLowerCase().substring(0, 2);
    const closeText = localeCode === 'de' ? 'Schließen' : (localeCode === 'no' || localeCode === 'nb' ? 'Lukk' : 'Close');

    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'sl-modal-overlay';
    
    modalOverlay.innerHTML = `
      <div class="sl-modal-container" style="max-width: 650px; border-radius: 24px; padding: 0; border: none; overflow-y: auto; overflow-x: hidden;">
        <button type="button" class="sl-modal-close" aria-label="${closeText}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="sl-modal-content" style="padding: 40px; display: flex; flex-direction: column; gap: 24px; text-align: left;">
          <h2 class="sl-modal-heading" style="margin: 0; font-size: 2.6rem; font-weight: 600; color: #111827; line-height: 1.3; padding-right: 32px;">
            ${item.title}
          </h2>
          
          ${
            (item.image_large || item.image)
              ? `<div class="sl-modal-media" style="width: 100%; height: 350px; background: #ffffff; border: 1px solid #f3f4f6; border-radius: 12px; overflow: hidden; display: flex; align-items: center; justify-content: center;">
                  <img src="${item.image_large || item.image}" alt="${item.title}" class="modal-image" style="width: 100%; height: 100%; object-fit: cover;">
                 </div>`
              : ''
          }
          
          <div class="sl-modal-text rte" style="font-size: 1.5rem; line-height: 1.6; color: #4b5563;">
            ${item.description_html || item.description || ''}
          </div>
        </div>
      </div>
    `;

    const closeHandler = () => {
      document.body.style.overflow = '';
      modalOverlay.remove();
    };

    modalOverlay.querySelector('.sl-modal-close').onclick = closeHandler;
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        closeHandler();
      }
    };

    document.body.appendChild(modalOverlay);
  }

  getEstimatedInstallationPrice(screens) {
    return window.ScreenluxEngine.getEstimatedInstallationPrice(screens, this.data.config || window.ScreenluxData?.config);
  }

  calculateTotals() {
    let screensTotal = 0;
    let oldScreensTotal = 0;
    this.state.screens.forEach((s) => {
      if (!s.valid) return; // Skip invalid screens from pricing totals

      const rawCost = window.ScreenluxEngine.calculateScreenPrice(s, this.data.config);
      const variant = window.ScreenluxEngine.matchVariant(rawCost, this.data.screens);

      // Use compare_at_price for total calculation
      const compareAtPrice = (variant && variant.compare_at_price > 0) ? variant.compare_at_price : (variant ? variant.price : 0);

      if (variant) {
        screensTotal += variant.price;
        oldScreensTotal += compareAtPrice;
      }
    });

    let installTotal = 0;
    let estimatedInstallTotal = 0;
    if (this.state.installationType === 'diy') {
      if (this.state.selectedBracketId && this.state.selectedBracketId !== 'unsure') {
        const b = this.data.brackets.find((x) => x.id == this.state.selectedBracketId);
        if (b) {
          installTotal = b.price * this.state.screens.length;
        }
      }
    } else if (this.state.installationType === 'professional') {
      // For professional, we don't add to the JS grandTotal
      // But we calculate the estimate for UI display.
      estimatedInstallTotal = this.getEstimatedInstallationPrice(this.state.screens);
      installTotal = 0;
    }

    let bracketsTotal = 0;

    let steuerungTotal = 0;
    if (this.state.steuerung) {
      Object.entries(this.state.steuerung).forEach(([id, qty]) => {
        const product = this.data.steuerung.find((p) => p.id == id);
        if (product) steuerungTotal += product.price * qty;
      });
    }

    let automatisierungTotal = 0;
    if (this.state.automatisierung) {
      Object.entries(this.state.automatisierung).forEach(([id, qty]) => {
        const product = this.data.automatisierung.find((p) => p.id == id);
        if (product) automatisierungTotal += product.price * qty;
      });
    }

    const extraTotal = bracketsTotal + steuerungTotal + automatisierungTotal;
    const grandTotal = screensTotal + installTotal + extraTotal;
    const oldGrandTotal = oldScreensTotal + installTotal + extraTotal;

    // Deposit option: pay depositPercent now, the balance before shipping.
    // Shopify computes the checkout charge per line; this is the same number
    // to the cent except for rounding across lines.
    const depositPlan = this.data.config && this.data.config.depositPlan;
    const depositPercent = depositPlan && depositPlan.percent > 0 && depositPlan.percent < 100 ? depositPlan.percent : 0;
    const depositActive = depositPercent > 0 && this.state.depositEnabled !== false;
    const depositTotal = depositActive ? Math.round((grandTotal * depositPercent) / 100) : grandTotal;
    const balanceTotal = grandTotal - depositTotal;
    return {
      depositPercent,
      depositActive,
      depositTotal,
      balanceTotal,
      screensTotal,
      oldScreensTotal,
      installTotal,
      bracketsTotal,
      steuerungTotal,
      automatisierungTotal,
      estimatedInstallTotal,
      grandTotal,
      oldGrandTotal,
    };
  }

  renderExtraCategorySummary(list, totalsData, dataKey, titleKey, expandedKey) {
    const itemsCount = Object.values(this.state[dataKey] || {}).reduce((a, b) => a + b, 0);
    const fmt = (cents) => `${(cents / 100).toFixed(2)} ${window.ScreenluxData?.config?.currencySymbol || '€'}`;
    
    if (itemsCount > 0) {
      const category = document.createElement('div');
      category.className = `summary-category ${this.state[expandedKey] ? 'expanded' : ''}`;

      const header = document.createElement('div');
      header.className = 'summary-row category-header';
      header.innerHTML = `
        <div class="category-title">
          <span>${titleKey} (${itemsCount})</span>
          <svg class="chevron-icon" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <span class="category-price">${fmt(totalsData)}</span>
      `;
      header.onclick = () => {
        this.state[expandedKey] = !this.state[expandedKey];
        this.render();
      };
      category.appendChild(header);

      if (this.state[expandedKey]) {
        const details = document.createElement('div');
        details.className = 'category-details';
        Object.entries(this.state[dataKey]).forEach(([id, qty]) => {
          const product = this.data[dataKey].find((p) => p.id == id);
          if (product) {
            const pPrice = product.price * qty;
            const detailRow = document.createElement('div');
            detailRow.className = 'summary-row detail-row';
            detailRow.innerHTML = `
              <span class="detail-label">${qty}x ${product.title}</span>
              <span class="detail-price">${fmt(pPrice)}</span>
            `;
            details.appendChild(detailRow);
          }
        });
        category.appendChild(details);
      }
      list.appendChild(category);
    }
  }

  renderOrderSummary() {
    const totals = this.calculateTotals();
    const fmt = (cents) => `${(cents / 100).toFixed(2)} ${window.ScreenluxData?.config?.currencySymbol || '€'}`;

    const section = document.createElement('div');
    section.className = 'order-summary-box margin-top-lg';
    section.innerHTML = `<label class="grouping-title">${window.ScreenluxTranslations.orderSummaryTitle}</label>`;

    const list = document.createElement('div');
    list.className = 'summary-list';

    // 1. Screens Category (Grouped)
    const screensCategory = document.createElement('div');
    screensCategory.className = `summary-category ${this.state.screensExpanded ? 'expanded' : ''}`;

    const screenCount = this.state.screens.length;
    const screensHeader = document.createElement('div');
    screensHeader.className = 'summary-row category-header';
    screensHeader.innerHTML = `
      <div class="category-title">
        <span>${window.ScreenluxTranslations.orderSummary.zipScreens} (${screenCount})</span>
        <svg class="chevron-icon" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <span class="category-price">${fmt(totals.screensTotal)}</span>
    `;
    screensHeader.onclick = () => {
      this.state.screensExpanded = !this.state.screensExpanded;
      this.render();
    };
    screensCategory.appendChild(screensHeader);

    if (this.state.screensExpanded) {
      const screensDetails = document.createElement('div');
      screensDetails.className = 'category-details';
      this.state.screens.forEach((s, i) => {
        const rawCost = window.ScreenluxEngine.calculateScreenPrice(s, this.data.config);
        const variant = window.ScreenluxEngine.matchVariant(rawCost, this.data.screens);
        const sPrice = variant ? variant.price : 0;
        const detailRow = document.createElement('div');
        detailRow.className = 'summary-row detail-row';
        detailRow.innerHTML = `
          <span class="detail-label">${s.name || `${window.ScreenluxTranslations?.screenSummary?.prefix || 'Screen'} ${i + 1}`}: ${s.width}×${s.height}mm</span>
          <span class="detail-price">${fmt(sPrice)}</span>
        `;
        screensDetails.appendChild(detailRow);
      });
      screensCategory.appendChild(screensDetails);
    }
    list.appendChild(screensCategory);

    // 1.5 Brackets Categories removed

    // 2. Steuerung Categories
    this.renderExtraCategorySummary(list, totals.steuerungTotal, 'steuerung', window.ScreenluxTranslations.orderSummary.steuerung, 'steuerungExpanded');

    const steuerungCount = Object.values(this.state.steuerung || {}).reduce((a, b) => a + b, 0);
    const smarthomeHub = (this.data.automatisierung || []).find((item) => item.handle === 'smarthome-hub');
    const hasSmarthomeHub = !!smarthomeHub && (this.state.automatisierung?.[smarthomeHub.id] || 0) > 0;
    if (steuerungCount === 0 && !hasSmarthomeHub) {
      const warningRow = document.createElement('div');
      warningRow.className = 'summary-row warning-row margin-bottom-sm';
      warningRow.style = 'color: #B45309; font-size: 13px; display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; background: #FFFBEB; border: 1px solid #FEF3C7; border-radius: 6px; margin-top: 4px;';
      warningRow.innerHTML = `
        <svg fill="currentColor" width="16" height="16" viewBox="0 0 20 20" style="flex-shrink:0; margin-top:1px;"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
        <span>${window.ScreenluxTranslations.orderSummary.warningNoSteuerung}</span>
      `;
      list.appendChild(warningRow);
    }
    
    // 3. Automatisierung Categories
    this.renderExtraCategorySummary(list, totals.automatisierungTotal, 'automatisierung', window.ScreenluxTranslations.orderSummary.automatisierung, 'automatisierungExpanded');

    // 3. Installation Category — only show in summary for DIY brackets (real cost)
    if (totals.installTotal > 0 && this.state.installationType === 'diy') {
      const installCategory = document.createElement('div');
      installCategory.className = `summary-category ${this.state.installationExpanded ? 'expanded' : ''}`;
      const label = window.ScreenluxTranslations.orderSummary.installation || 'Montage';

      const installHeader = document.createElement('div');
      installHeader.className = 'summary-row category-header';
      installHeader.innerHTML = `
        <div class="category-title">
          <span>${label}</span>
          <svg class="chevron-icon" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <span class="category-price">${fmt(totals.installTotal)}</span>
      `;
      installHeader.onclick = () => {
        this.state.installationExpanded = !this.state.installationExpanded;
        this.render();
      };
      installCategory.appendChild(installHeader);
      list.appendChild(installCategory);
    }

    // 4. Total (excludes professional installation — that's billed separately)
    const totalRow = document.createElement('div');
    totalRow.className = 'summary-row total';
    totalRow.innerHTML = `
      <span>${window.ScreenluxTranslations.orderSummary.total}</span>
      <div class="total-price-wrapper">
         ${totals.oldGrandTotal > totals.grandTotal ? `<span class="price-old">${fmt(totals.oldGrandTotal)}</span>` : ''} 
         <span class="price-current">${fmt(totals.grandTotal)}</span>
      </div>
    `;
    list.appendChild(totalRow);

    // 4a. Payment choice: deposit now vs. everything now. Only rendered while
    // Powerups has a deposit plan attached to the products.
    if (totals.depositPercent > 0) {
      const t = window.ScreenluxTranslations.deposit || {};
      const depositAmount = Math.round((totals.grandTotal * totals.depositPercent) / 100);
      const option = (active, enabled, title, desc, amount) => `
        <button type="button" class="deposit-option ${active ? 'active' : ''}" data-deposit="${enabled ? '1' : '0'}" aria-pressed="${active}">
          <span class="deposit-radio" aria-hidden="true"></span>
          <span class="deposit-option-text"><strong>${title}</strong>${desc ? `<span>${desc}</span>` : ''}</span>
          <span class="deposit-amount">${fmt(amount)}</span>
        </button>`;
      const block = document.createElement('div');
      block.className = 'deposit-options';
      block.innerHTML = `
        <div class="deposit-title">${t.title || 'Zahlung'}</div>
        ${option(totals.depositActive, true, `${totals.depositPercent} % ${t.optionDeposit || 'Anzahlung'}`, t.optionDepositDesc || '', depositAmount)}
        ${option(!totals.depositActive, false, t.optionFull || 'Gesamtbetrag jetzt zahlen', t.optionFullDesc || '', totals.grandTotal)}
        ${totals.depositActive && t.balanceNote ? `<p class="deposit-note">${t.balanceNote.replace('{amount}', fmt(totals.balanceTotal))}</p>` : ''}
      `;
      block.querySelectorAll('.deposit-option').forEach((el) => {
        el.onclick = () => {
          this.state.depositEnabled = el.dataset.deposit === '1';
          this.saveStateToStorage();
          this.render();
        };
      });
      list.appendChild(block);
    }

    // 4b. Professional Installation — separate card below total
    if (this.state.installationType === 'professional' && totals.estimatedInstallTotal > 0) {
      const installCard = document.createElement('div');
      installCard.className = 'installation-separate-card margin-top-md';
      installCard.innerHTML = `
        <div class="installation-separate-header">
          <div class="installation-separate-info">
            <span class="installation-separate-title">${window.ScreenluxTranslations.orderSummary.professionalInstallation}</span>
            <span class="installation-separate-price">~ ${fmt(totals.estimatedInstallTotal)}</span>
          </div>
        </div>
        <p class="installation-separate-desc">${window.ScreenluxTranslations.orderSummary?.installationNotice || 'Wird separat durch unseren Montagepartner in Rechnung gestellt nach Aufmaß vor Ort.'}</p>
      `;
      list.appendChild(installCard);
    }

    section.appendChild(list);

    // 5. Shipping Info
    const shippingInfo = document.createElement('div');
    shippingInfo.className = 'shipping-info-row';

    // Shipping disclosure. Previously this only ever named the free-shipping
    // threshold, so a customer below it was told what they would NOT get and
    // never what they were about to be charged. It also freed EUR shipping at
    // €1,000 while Shopify frees it above €960, telling customers in that band
    // to spend more when they already qualified.
    const currencyCode = window.ScreenluxData?.config?.currencyCode;
    const isDe = window.ScreenluxData?.locale === 'de';
    const shippingConfig = SHIPPING_TIERS[currencyCode];

    // Currencies without a configured zone here (e.g. CHF) keep the previous
    // generic copy rather than guessing at a rate.
    let deliveryStatusText = window.ScreenluxTranslations.orderSummary.freeDelivery;
    let deliveryIsFree = true;

    if (shippingConfig) {
      const { tiers, freeThreshold } = shippingConfig;
      const tier = tiers.find((t) => totals.grandTotal <= t.maxTotal) || tiers[tiers.length - 1];
      deliveryIsFree = tier.price === 0;

      const locale = currencyCode === 'NOK' ? 'nb-NO' : (isDe ? 'de-DE' : 'en-GB');
      const symbol = currencyCode === 'NOK' ? 'kr' : '€';
      const money = (minor) => {
        const units = minor / 100;
        const decimals = units % 1 === 0 ? 0 : 2;
        const n = units.toLocaleString(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
        return `${n} ${symbol}`;
      };

      const charge = money(tier.price);
      const threshold = money(freeThreshold.display);

      if (currencyCode === 'NOK') {
        const from = freeThreshold.inclusive ? 'fra' : 'over';
        deliveryStatusText = deliveryIsFree
          ? 'Gratis frakt'
          : `Frakt ${charge} · gratis ${from} ${threshold}`;
      } else if (isDe) {
        const from = freeThreshold.inclusive ? 'ab' : 'über';
        deliveryStatusText = deliveryIsFree
          ? 'Kostenlose Lieferung'
          : `Versand ${charge} · kostenlos ${from} ${threshold}`;
      } else {
        const from = freeThreshold.inclusive ? 'from' : 'over';
        deliveryStatusText = deliveryIsFree
          ? 'Free delivery'
          : `Shipping ${charge} · free ${from} ${threshold}`;
      }
    }

    // Live delivery estimate: reflects current backlog, stock and incoming
    // POs from the Powerups app. The whole order ships together, so with
    // several differently-configured screens the estimate is driven by
    // whichever one is slowest. Falls back to the static locale copy
    // instantly, then upgrades in place once the (debounced) fetch resolves.
    let arrivesInText = window.ScreenluxTranslations.orderSummary.arrivesIn;
    if (typeof this.formatDeliveryTime === 'function') {
      arrivesInText = this.formatDeliveryTime(arrivesInText, window.ScreenluxData.locale);
    }

    if (this.state.screens && this.state.screens.length > 0) {
      const market = this.getDeliveryEstimateMarket();
      const screensConfig = this.state.screens.map((s) => this.getScreenEstimateConfig(s));
      const signature = `${market}|${JSON.stringify(screensConfig)}`;

      if (this.state.liveDeliveryEstimateKey === signature && this.state.liveDeliveryEstimate?.range) {
        arrivesInText = this.formatLiveEstimateText(
          this.state.liveDeliveryEstimate.range,
          window.ScreenluxData.locale,
          this.state.liveDeliveryEstimate.nextOrderWindow
        );
      } else {
        this.scheduleLiveDeliveryEstimate(signature, screensConfig, market);
      }
    }

    shippingInfo.innerHTML = `
      <span class="shipping-icon">
        <img src="${this.data.assets.truck_icon}" alt="Delivery" width="24" height="24">
      </span>
      <span class="shipping-text"><strong${deliveryIsFree ? ' class="text-success"' : ''}>${deliveryStatusText}</strong> · ${arrivesInText}</span>
    `;
    section.appendChild(shippingInfo);

    // 6. Checkout Button
    const allValid = this.state.screens.every((s) => s.valid);
    const cartBtn = document.createElement('button');
    cartBtn.className = `btn btn-primary margin-top-md ${!allValid ? 'btn-disabled' : ''}`;
    let cartLabel = window.ScreenluxTranslations.orderSummary.pleaseCheckDimensions;
    if (allValid) {
      cartLabel = totals.depositActive
        ? `${window.ScreenluxTranslations.orderSummary.continueToPayment} — ${(window.ScreenluxTranslations.deposit && window.ScreenluxTranslations.deposit.today) || 'heute'} ${fmt(totals.depositTotal)}`
        : `${window.ScreenluxTranslations.orderSummary.continueToPayment} — ${fmt(totals.grandTotal)}`;
    }
    cartBtn.innerText = cartLabel;
    cartBtn.onclick = allValid ? this.handleAddToCart : null;

    section.appendChild(cartBtn);

    // 7. German Design Award quote + feature bullets (same white card, under the CTA)
    const awards = window.ScreenluxTranslations?.awards || {};

    if (awards.image || awards.quote) {
      const awardSection = document.createElement('div');
      awardSection.className = 'pdp-award-section';
      awardSection.innerHTML = `
        <img src="${awards.image || ''}" alt="German Design Award" class="pdp-award-logo" width="72" height="72" loading="lazy">
        ${awards.quote ? `<blockquote class="pdp-award-quote">${awards.quote}</blockquote>` : ''}
      `;
      section.appendChild(awardSection);
    }

    const bulletIcons = [
      [awards.bullet1, this.data.assets?.feature_icon_sun],
      [awards.bullet2, this.data.assets?.feature_icon_wind],
      [awards.bullet3, this.data.assets?.feature_icon_montage],
    ];
    const featuresList = document.createElement('ul');
    featuresList.className = 'pdp-features-list';
    bulletIcons.forEach(([text, icon]) => {
      if (!text) return;
      const li = document.createElement('li');
      li.className = 'pdp-feature-item';
      li.innerHTML = `<img src="${icon || ''}" alt="" class="pdp-feature-icon" width="24" height="24" loading="lazy"><span class="pdp-feature-text">${text}</span>`;
      featuresList.appendChild(li);
    });
    if (awards.bullet4) {
      const li = document.createElement('li');
      li.className = 'pdp-feature-item';
      li.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="color: #1f2937; flex-shrink: 0;" class="pdp-feature-icon" xmlns="http://www.w3.org/2000/svg"><path d="M19.5 3.75H4.5C4.10218 3.75 3.72064 3.90804 3.43934 4.18934C3.15804 4.47064 3 4.85218 3 5.25V10.5C3 15.4425 5.3925 18.4378 7.39969 20.0803C9.56156 21.8484 11.7122 22.4484 11.8059 22.4738C11.9348 22.5088 12.0708 22.5088 12.1997 22.4738C12.2934 22.4484 14.4413 21.8484 16.6059 20.0803C18.6075 18.4378 21 15.4425 21 10.5V5.25C21 4.85218 20.842 4.47064 20.5607 4.18934C20.2794 3.90804 19.8978 3.75 19.5 3.75ZM19.5 10.5C19.5 13.9753 18.2194 16.7962 15.6937 18.8831C14.5943 19.7885 13.344 20.493 12 20.9644C10.6736 20.5012 9.4387 19.8092 8.35125 18.9197C5.79563 16.8291 4.5 13.9969 4.5 10.5V5.25H19.5V10.5ZM7.71937 13.2806C7.57864 13.1399 7.49958 12.949 7.49958 12.75C7.49958 12.551 7.57864 12.3601 7.71937 12.2194C7.86011 12.0786 8.05098 11.9996 8.25 11.9996C8.44902 11.9996 8.63989 12.0786 8.78063 12.2194L10.5 13.9397L15.2194 9.21937C15.2891 9.14969 15.3718 9.09442 15.4628 9.0567C15.5539 9.01899 15.6515 8.99958 15.75 8.99958C15.8485 8.99958 15.9461 9.01899 16.0372 9.0567C16.1282 9.09442 16.2109 9.14969 16.2806 9.21937C16.3503 9.28906 16.4056 9.37178 16.4433 9.46283C16.481 9.55387 16.5004 9.65145 16.5004 9.75C16.5004 9.84855 16.481 9.94613 16.4433 10.0372C16.4056 10.1282 16.3503 10.2109 16.2806 10.2806L11.0306 15.5306C10.961 15.6004 10.8783 15.6557 10.7872 15.6934C10.6962 15.7312 10.5986 15.7506 10.5 15.7506C10.4014 15.7506 10.3038 15.7312 10.2128 15.6934C10.1217 15.6557 10.039 15.6004 9.96937 15.5306L7.71937 13.2806Z" fill="currentColor"/></svg><span class="pdp-feature-text">${awards.bullet4}</span>`;
      featuresList.appendChild(li);
    }
    if (featuresList.children.length) section.appendChild(featuresList);

    return section;
  }

  renderStickyBar() {
    const bar = document.createElement('div');
    bar.className = 'sticky-summary-bar';
    bar.innerHTML = `
      <div class="sticky-bar-left">
        <div class="sticky-bar-title">Gesamtsumme</div>
        <div class="sticky-bar-price-wrapper">
          <span class="sticky-bar-price-old"></span>
          <span class="sticky-bar-price"></span>
          <svg class="sticky-bar-arrow hidden" width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.0625 1.46875L7.3125 7.71875L13.5625 1.46875" stroke="#171717" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
      <div class="sticky-bar-right">
        <button type="button" class="sticky-bar-btn">${window.ScreenluxTranslations?.orderSummary?.continueToPayment || 'Continue to payment'}</button>
      </div>
    `;

    const scrollHandler = (e) => {
      e.preventDefault();
      const summaryBox = this.querySelector('.order-summary-box');
      if (summaryBox) {
        summaryBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const arrowBtn = bar.querySelector('.sticky-bar-arrow');
    const ctaBtn = bar.querySelector('.sticky-bar-btn');
    
    if (arrowBtn) arrowBtn.addEventListener('click', scrollHandler);
    if (ctaBtn) ctaBtn.addEventListener('click', scrollHandler);
    
    return bar;
  }

  updateStickyBar() {
    const bar = this.querySelector('.sticky-summary-bar');
    if (!bar) return;

    // Fade out if scrolled into order-summary-box
    const orderSummaryBox = this.querySelector('.order-summary-box');
    let isHidden = false;
    
    if (orderSummaryBox) {
      const summaryRect = orderSummaryBox.getBoundingClientRect();
      // Hide the bar when the user reaches the very bottom 
      // i.e., top of summary box is above the bottom of the viewport
      if (summaryRect.top < window.innerHeight - 80) {
        isHidden = true;
      }
    }

    if (isHidden) {
      bar.classList.add('sticky-summary-bar--hidden');
      return; 
    } else {
      bar.classList.remove('sticky-summary-bar--hidden');
    }

    const screensGroup = this.querySelector('.screens-list');
    let isPastScreens = false;
    
    if (screensGroup) {
      const rect = screensGroup.getBoundingClientRect();
      // If the bottom of the screens list is above the window's vertical center (or top), we are past it.
      // E.g. when scrolling down to the Installation section.
      if (rect.bottom < window.innerHeight / 2) {
        isPastScreens = true;
      }
    }

    const titleEl = bar.querySelector('.sticky-bar-title');
    const oldPriceEl = bar.querySelector('.sticky-bar-price-old');
    const priceEl = bar.querySelector('.sticky-bar-price');
    const arrowEl = bar.querySelector('.sticky-bar-arrow');
    const fmt = (cents) => `${(cents / 100).toFixed(0)} ${window.ScreenluxData?.config?.currencySymbol || '€'}`;

    if (isPastScreens) {
      const totals = this.calculateTotals();
      titleEl.innerText = window.ScreenluxTranslations?.orderSummary?.total || 'Gesamtsumme';
      
      if (totals.oldGrandTotal > totals.grandTotal) {
        oldPriceEl.innerText = fmt(totals.oldGrandTotal);
        oldPriceEl.style.display = 'inline';
      } else {
        oldPriceEl.style.display = 'none';
      }
      priceEl.innerText = fmt(totals.grandTotal);
      arrowEl.classList.remove('hidden');
    } else {
      // Find active screen
      const activeScreenIndex = this.state.screens.findIndex((s) => s.expanded);
      const screenIndex = activeScreenIndex >= 0 ? activeScreenIndex : 0;
      const screen = this.state.screens[screenIndex];
      
      if (screen) {
        titleEl.innerText = screen.name || `${window.ScreenluxTranslations?.screenSummary?.prefix || 'Screen'} ${screenIndex + 1}`;
        
        const rawCost = window.ScreenluxEngine.calculateScreenPrice(screen, this.data.config);
        const variant = window.ScreenluxEngine.matchVariant(rawCost, this.data.screens);
        const price = variant ? variant.price : rawCost;
        const compareAtPrice = variant ? (variant.compare_at_price || price) : price;
        
        if (compareAtPrice > price) {
          oldPriceEl.innerText = fmt(compareAtPrice);
          oldPriceEl.style.display = 'inline';
        } else {
          oldPriceEl.style.display = 'none';
        }
        priceEl.innerText = fmt(price);
      }
      arrowEl.classList.add('hidden');
    }
  }

  /**
   * Frühbucher campaign (until 2026-07-26 23:59 Europe/Berlin): week-based
   * delivery estimates carry the 30%-discount justification. Returns null
   * once the campaign is over so every caller falls back to the regular
   * phrasing without a second deploy.
   */
  campaignDeliveryText(span, locale) {
    if (Date.now() >= Date.parse('2026-07-27T00:00:00+02:00')) return null;
    if (locale === 'no' || locale === 'nb') {
      return `Levering innen ${span} uker. På grunn av høy etterspørsel er produksjonen vår for tiden fullt belastet, noe som fører til lengre leveringstid.`;
    }
    if (locale === 'de') {
      return `Lieferung in ${span} Wochen. Aufgrund der aktuell hohen Nachfrage ist unsere Produktion voll ausgelastet, was zu einer längeren Lieferzeit führt.`;
    }
    return `Delivery in ${span} weeks. Due to high demand our production is currently at full capacity, which leads to a longer delivery time.`;
  }

  formatDeliveryTime(text, locale) {
    if (!text) return '';
    const cleanText = text.trim();

    // Normalize input (e.g. if it's "6 weeks" or "6")
    const matchNumber = cleanText.match(/^(\d+)\s*(?:weeks?|uker?|Wochen?)?$/i);
    if (matchNumber) {
      const num = matchNumber[1];
      const campaign = this.campaignDeliveryText(num, locale);
      if (campaign) return campaign;
      if (locale === 'no' || locale === 'nb') {
        return `${num} ukers levering`;
      } else if (locale === 'de') {
        return `Lieferung in ${num} Wochen`;
      } else {
        return `${num} weeks delivery`;
      }
    }

    // If it's a range like "5-7 weeks" or "5-7"
    const matchRange = cleanText.match(/^(\d+-\d+)\s*(?:weeks?|uker?|Wochen?)?$/i);
    if (matchRange) {
      const range = matchRange[1];
      const campaign = this.campaignDeliveryText(range, locale);
      if (campaign) return campaign;
      if (locale === 'no' || locale === 'nb') {
        return `${range} ukers levering`;
      } else if (locale === 'de') {
        return `Lieferung in ${range} Wochen`;
      } else {
        return `${range} weeks delivery`;
      }
    }

    // Full sentences like "Lieferung in 4-6 Wochen" (the static locale
    // fallback shown before the live estimate resolves): still swap in the
    // campaign copy as long as the range is week-based.
    const matchEmbedded = cleanText.match(/(\d+\s*[-–]\s*\d+)\s*(?:weeks?|uker|Wochen)/i);
    if (matchEmbedded) {
      const campaign = this.campaignDeliveryText(matchEmbedded[1].replace(/\s*[-–]\s*/, '–'), locale);
      if (campaign) return campaign;
    }

    // Default: return the text as-is
    return text;
  }

  /**
   * Market used for the live delivery estimate. Mirrors the currency-based
   * heuristic already used a few lines up for the free-delivery threshold
   * text (NOK -> Norway, everything else -> Germany).
   */
  getDeliveryEstimateMarket() {
    const isNorway = window.ScreenluxData?.config?.currencyCode === 'NOK';
    return isNorway ? 'no' : 'de';
  }

  /**
   * Map one configured screen to the shape the Powerups app's delivery
   * estimate endpoint expects. Frame colour ids match buildDynamicImageUrl's
   * frameCodeMap; cassette size matches the 'large' -> 120mm convention used
   * throughout this file.
   */
  getScreenEstimateConfig(screen) {
    const frameCodeMap = { anthracite: 'AN', white: 'WH', black: 'BL' };
    return {
      motor: screen.motor === 'wired' ? 'wired' : 'solar',
      color: frameCodeMap[screen.frameColor] || 'AN',
      size: screen.cassetteSize === 'large' ? '120' : '85',
      width: screen.width || 2000,
      qty: 1
    };
  }

  /**
   * Localised sentence for a live { unit, min, max } estimate range, matching
   * the phrasing conventions already used by formatDeliveryTime() above
   * (which only ever assumes weeks) — this variant also covers the shorter
   * day-based ranges the live estimate can return.
   *
   * nextOrderWindow is the merchant-set "otherwise not available again until
   * X" value from the Powerups delivery-estimate admin page (same value for
   * every market) — only surfaced once the estimate itself signals high
   * demand (6+ weeks), and only if the merchant has actually set it.
   */
  formatLiveEstimateText(range, locale, nextOrderWindow) {
    if (!range) return '';
    const span = `${range.min}–${range.max}`;
    const isDays = range.unit === 'days';
    // Frühbucher campaign copy replaces the regular week-based phrasing
    // (including the high-demand suffixes, which it already implies).
    if (!isDays) {
      const campaign = this.campaignDeliveryText(span, locale);
      if (campaign) return campaign;
    }
    // High-demand note: only meaningful once the estimate reaches multi-week
    // territory, so it never fires for the short day-based ranges.
    const isHighDemand = !isDays && range.max >= 6;
    const hasNextWindow = isHighDemand && !!nextOrderWindow;
    if (locale === 'no' || locale === 'nb') {
      const base = isDays ? `${span} dagers levering` : `${span} ukers levering`;
      if (hasNextWindow) return `Høy etterspørsel – bestill nå for levering om ${span} uker, ellers ikke igjen før ${nextOrderWindow}`;
      return isHighDemand ? `${base} på grunn av høy etterspørsel` : base;
    }
    if (locale === 'de') {
      const base = isDays ? `Lieferung in ${span} Tagen` : `Lieferung in ${span} Wochen`;
      if (hasNextWindow) return `Hohe Nachfrage – jetzt sichern für Lieferung in ${span} Wochen, sonst erst wieder ab ${nextOrderWindow}`;
      return isHighDemand ? `${base} aufgrund hoher Nachfrage` : base;
    }
    const base = isDays ? `${span} days delivery` : `${span} weeks delivery`;
    if (hasNextWindow) return `High demand – order now for delivery in ${span} weeks, otherwise not available again until ${nextOrderWindow}`;
    return isHighDemand ? `${base} due to high demand` : base;
  }

  /**
   * Kick off (debounced) a fetch of the live delivery estimate for the
   * current screen configuration. Cheap to call on every render — it no-ops
   * unless the configuration signature actually changed since the last
   * completed fetch.
   */
  scheduleLiveDeliveryEstimate(signature, screensConfig, market) {
    if (this._deliveryEstimateScheduledFor === signature) return;
    this._deliveryEstimateScheduledFor = signature;
    if (this._deliveryEstimateDebounceTimer) clearTimeout(this._deliveryEstimateDebounceTimer);
    this._deliveryEstimateDebounceTimer = setTimeout(() => {
      this.fetchLiveDeliveryEstimate(signature, screensConfig, market);
    }, 500);
  }

  async fetchLiveDeliveryEstimate(signature, screensConfig, market) {
    try {
      const params = new URLSearchParams({
        action: 'get-delivery-estimate',
        market,
        screens: JSON.stringify(screensConfig)
      });
      const res = await fetch(`/apps/measurements?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        this.state.liveDeliveryEstimate = data;
        this.state.liveDeliveryEstimateKey = signature;
        this.render();
      } else {
        // Let a future render() retry this exact configuration instead of
        // getting stuck on the static fallback until the config changes.
        this._deliveryEstimateScheduledFor = null;
      }
    } catch (err) {
      console.error('Failed to fetch live delivery estimate:', err);
      this._deliveryEstimateScheduledFor = null;
    }
  }

  // /cart/clear.js empties the lines but keeps the cart attributes. Anything
  // an earlier visit left there travels with this order: the _draft_order
  // marker from a quote's deposit link (the Powerups webhook would tag that
  // draft as converted), or the Aufmaß attributes of an abandoned beta
  // configurator attempt. A configurator order starts from a clean cart, so
  // blank every attribute that exists. Never block the checkout over this.
  async resetCartAttributes() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const attributes = {};
      Object.keys(cart.attributes || {}).forEach((key) => {
        attributes[key] = '';
      });
      attributes._draft_order = '';
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (err) {
      console.error('Failed to reset cart attributes:', err);
    }
  }

  handleAddToCart = () => {
    const payload = window.ScreenluxEngine.generateCartPayload(this.state, this.data);
    const cartUrlInput = this.querySelector('input[name="cart-add-url"]');
    const cartUrl = cartUrlInput ? cartUrlInput.value : '/cart/add';

    console.log('Adding to cart:', payload);

    try {
      localStorage.removeItem('screenlux_configurator_state');
    } catch (e) {
      console.error('Failed to clear configurator state from localStorage:', e);
    }

    // Clear the cart first to prevent duplicate configurations in cart
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        return fetch(cartUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      })
      .then((r) => r.json())
      .then(() => this.resetCartAttributes())
      .then(() => {
        window.location.href = '/checkout';
      })
      .catch((err) => {
        console.error('Cart Error:', err);
      });
  };
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
